import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  useReactFlow,
  Handle,
  Position,
  MarkerType,
  getBezierPath,
  BaseEdge
} from 'reactflow';
import 'reactflow/dist/style.css';
import styled, { keyframes, css } from 'styled-components';
import axios from 'axios';
import {
  Zap, Save, Play, Trash2, Settings, X, Instagram, Mail, Clock, Smartphone, 
  Heart, UserMinus, Eye, Send, Search, RefreshCw, Calendar,
  GitBranch, CalendarDays, List, Plus, Edit3, ToggleLeft, ToggleRight,
  GanttChart, Activity, Timer, CheckCircle2, XCircle,
  BarChart3, History, Square, ChevronRight, Monitor, Users, Pause, Cast
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import NodePropertiesPanel from '../components/workflow/NodePropertiesPanel';
import { getAllNodeTypes } from '../config/nodeRegistry';
import Sessions from './Sessions';
import WorkflowSessionTimeline from '../components/workflow/WorkflowSessionTimeline';
import ScriptFlows from '../components/workflow/ScriptFlows';
import { getProfilePicUrl, getLocalProfilePicUrl } from '../config/imageUrls';
import { useAuth } from '../contexts/AuthContext';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const WorkflowContainer = styled.div`
  width: 100%;
  height: calc(100vh - 40px);
  background: var(--bg);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const WorkflowTitle = styled.input`
  background: transparent;
  border: none;
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 700;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background 0.2s;
  &:hover { background: var(--surface); }
  &:focus { outline: none; background: var(--surface); }
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${p => p.$primary ? 'linear-gradient(135deg, var(--primary), var(--secondary))' : p.$danger ? 'rgba(239,68,68,.15)' : 'var(--card-border)'};
  border: none;
  border-radius: 8px;
  color: ${p => p.$danger ? '#ef4444' : 'var(--text-primary)'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { transform: translateY(-1px); opacity: .9; }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const FlowWrapper = styled.div`
  flex: 1;
  position: relative;
  background: var(--bg);
`;

const SidebarPanel = styled.div`
  position: absolute;
  left: 20px;
  top: 20px;
  width: 200px;
  max-height: calc(100% - 40px);
  overflow-y: auto;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 10px;
  padding: 10px;
  z-index: 10;
  box-shadow: 0 4px 20px rgba(0,0,0,.3);
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

const SidebarTitle = styled.h3`
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NodeTypesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const NodeTypeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  background: var(--surface);
  border: 1.5px solid ${p => p.$color || 'var(--card-border)'};
  border-radius: 6px;
  cursor: grab;
  transition: all 0.15s;
  &:hover { background: var(--card-border); transform: translateX(2px); }
  &:active { cursor: grabbing; }
`;

const SidebarNodeIcon = styled.div`
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 6px;
  background: ${p => p.$color || 'var(--primary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  svg { width: 14px; height: 14px; }
`;

const NodeInfo = styled.div`
  flex: 1;
`;

const NodeTypeName = styled.div`
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
`;

const NodeTypeDesc = styled.div`
  color: var(--text-secondary);
  font-size: 10px;
  line-height: 1.2;
  display: none;
`;

/* ── Custom Node Styles ── */
const nodeRunPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,.5), 0 4px 16px rgba(0,0,0,.25); border-color: #22c55e; }
  50% { box-shadow: 0 0 0 6px rgba(34,197,94,.15), 0 0 20px rgba(34,197,94,.3), 0 4px 16px rgba(0,0,0,.25); border-color: #4ade80; }
`;

const CustomNodeContainer = styled.div`
  background: var(--card-bg);
  border: 2px solid ${p => p.$running ? '#22c55e' : p.$color || 'var(--primary)'};
  border-radius: 12px;
  padding: ${p => p.$compact ? '10px 12px' : '14px 16px'};
  width: ${p => p.$compact ? '160px' : p.$fixedWidth ? '220px' : 'auto'};
  min-width: ${p => p.$compact ? '140px' : '200px'};
  box-shadow: 0 4px 16px rgba(0,0,0,.25);
  transition: all 0.2s;
  position: relative;
  animation: ${p => p.$running ? nodeRunPulse : 'none'} 1.5s ease-in-out infinite;
  &:hover { box-shadow: 0 6px 24px rgba(0,0,0,.35); }
`;

const RunningBadge = styled.div`
  position: absolute; top: -8px; right: -8px;
  background: #22c55e; color: #fff;
  font-size: 9px; font-weight: 800;
  padding: 2px 7px; border-radius: 8px;
  letter-spacing: .5px;
  box-shadow: 0 2px 8px rgba(34,197,94,.4);
  display: flex; align-items: center; gap: 3px;
  opacity: ${p => p.$visible ? 1 : 0};
  pointer-events: ${p => p.$visible ? 'auto' : 'none'};
  transition: opacity 0.2s;
`;

const NodeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const NodeIconWrapper = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${p => p.$color || 'var(--primary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
`;

const NodeLabel = styled.div`
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  flex: 1;
`;

const NodeBtn = styled.button`
  background: ${p => p.$danger ? 'rgba(239,68,68,.15)' : 'var(--surface)'};
  border: none;
  border-radius: 6px;
  padding: 5px;
  color: ${p => p.$danger ? '#ef4444' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: all 0.15s;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: ${p => p.$danger ? 'rgba(239,68,68,.3)' : 'var(--surface-hover)'}; color: ${p => p.$danger ? '#ef4444' : 'var(--text-primary)'}; }
`;

const NodeSubtext = styled.div`
  font-size: 11px;
  color: var(--text-muted);
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ConfigBadge = styled.span`
  color: var(--success);
  font-weight: 600;
`;

const NodeInfoRow = styled.div`
  display: flex; align-items: center; gap: 8px; margin-top: 8px;
  padding: 6px 8px; background: rgba(0,0,0,.15); border-radius: 8px;
`;

const NodeAvatar = styled.img`
  width: 36px; height: 36px; border-radius: 50%; object-fit: cover;
  border: 2.5px solid ${p => p.$color || 'var(--primary)'};
  flex-shrink: 0;
`;

const NodeAvatarFallback = styled.div`
  width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: ${p => p.$bg || 'rgba(165,145,239,.15)'};
  color: ${p => p.$color || 'var(--primary)'};
  border: 2.5px solid ${p => p.$color || 'var(--primary)'};
`;

const NodeInfoText = styled.div`
  flex: 1; min-width: 0;
  .ni-user { font-size: 12px; font-weight: 700; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ni-device { font-size: 10px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
`;

const ScriptBadge = styled.div`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 10px; border-radius: 8px; font-size: 10px; font-weight: 700;
  background: ${p => p.$bg || 'rgba(165,145,239,.12)'};
  color: ${p => p.$color || '#a591ef'};
  margin-top: 6px; letter-spacing: .3px;
`;

/* ── Execution Status Panel ── */
const execPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: .5; }
`;

const ExecPanel = styled.div`
  position: absolute;
  right: 20px;
  bottom: 20px;
  width: 320px;
  max-height: 400px;
  overflow-y: auto;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 14px 16px;
  z-index: 10;
  box-shadow: 0 4px 20px rgba(0,0,0,.3);
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

const ExecHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const ExecTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExecStatusBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: .5px;
  background: ${p =>
    p.$status === 'running' ? 'rgba(34,197,94,.15)' :
    p.$status === 'completed' ? 'rgba(59,130,246,.15)' :
    p.$status === 'cancelled' ? 'rgba(234,179,8,.15)' :
    'rgba(239,68,68,.15)'};
  color: ${p =>
    p.$status === 'running' ? '#22c55e' :
    p.$status === 'completed' ? '#3b82f6' :
    p.$status === 'cancelled' ? '#eab308' :
    '#ef4444'};
  animation: ${p => p.$status === 'running' ? execPulse : 'none'} 1.5s ease-in-out infinite;
`;

const ExecStopBtn = styled.button`
  background: rgba(239,68,68,.15);
  border: none;
  border-radius: 6px;
  padding: 4px 10px;
  color: #ef4444;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all .15s;
  &:hover { background: rgba(239,68,68,.3); }
`;

const ExecStepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ExecStep = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: ${p =>
    p.$status === 'running' ? 'rgba(34,197,94,.08)' :
    p.$status === 'completed' ? 'rgba(59,130,246,.05)' :
    p.$status === 'failed' ? 'rgba(239,68,68,.08)' :
    p.$status === 'cancelled' ? 'rgba(234,179,8,.05)' :
    'var(--surface)'};
  border-radius: 8px;
  border-left: 3px solid ${p =>
    p.$status === 'running' ? '#22c55e' :
    p.$status === 'completed' ? '#3b82f6' :
    p.$status === 'failed' ? '#ef4444' :
    p.$status === 'cancelled' ? '#eab308' :
    'var(--card-border)'};
`;

const ExecStepDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${p =>
    p.$status === 'running' ? '#22c55e' :
    p.$status === 'completed' ? '#3b82f6' :
    p.$status === 'failed' ? '#ef4444' :
    p.$status === 'cancelled' ? '#eab308' :
    'var(--text-muted)'};
  animation: ${p => p.$status === 'running' ? execPulse : 'none'} 1s ease-in-out infinite;
`;

const ExecStepLabel = styled.div`
  flex: 1;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ExecStepTime = styled.div`
  font-size: 10px;
  color: var(--text-muted);
  white-space: nowrap;
`;

const ExecProgress = styled.div`
  height: 4px;
  background: var(--surface);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 10px;
`;

/* ── Bottom Execution History Panel ── */
const BottomHistoryPanel = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0;
  background: var(--card-bg); border-top: 1px solid var(--card-border);
  z-index: 10; max-height: ${p => p.$collapsed ? '36px' : '420px'}; overflow: hidden;
  transition: max-height .3s ease; box-shadow: 0 -2px 12px rgba(0,0,0,.15);
`;
const BottomHistoryHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 16px; cursor: pointer; user-select: none;
  &:hover { background: var(--surface); }
`;
const BottomHistoryTitle = styled.div`
  font-size: 12px; font-weight: 700; color: var(--text-primary);
  display: flex; align-items: center; gap: 8px;
`;
const HistorySplitLayout = styled.div`
  display: flex; height: 370px; overflow: hidden;
`;
const HistoryListPane = styled.div`
  flex: ${p => p.$hasDetail ? '0 0 420px' : '1'};
  overflow-y: auto; padding: 0 0 8px; border-right: ${p => p.$hasDetail ? '1px solid var(--border)' : 'none'};
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;
const HistoryDetailPane = styled.div`
  flex: 1; overflow-y: auto; padding: 12px 16px; background: rgba(0,0,0,.08);
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;
const HistoryRow = styled.div`
  display: grid; grid-template-columns: 30px 100px 68px 90px 1fr;
  gap: 6px; padding: 5px 12px; align-items: center; font-size: 10px;
  border-bottom: 1px solid var(--border); cursor: pointer;
  background: ${p => p.$active ? 'rgba(59,130,246,.08)' : 'transparent'};
  border-left: 3px solid ${p => p.$active ? '#3b82f6' : 'transparent'};
  &:first-child { font-weight: 700; color: var(--text-muted); font-size: 9px; text-transform: uppercase; cursor: default; border-left-color: transparent; }
  &:hover:not(:first-child) { background: var(--surface); }
`;
const HistoryStepChip = styled.span`
  display: inline-flex; align-items: center; gap: 3px;
  padding: 2px 6px; border-radius: 4px; font-size: 9px; font-weight: 700;
  white-space: nowrap;
  background: ${p =>
    p.$status === 'completed' ? 'rgba(59,130,246,.12)' :
    p.$status === 'failed' ? 'rgba(239,68,68,.12)' :
    p.$status === 'cancelled' ? 'rgba(234,179,8,.12)' :
    p.$status === 'running' ? 'rgba(34,197,94,.12)' :
    'var(--surface)'};
  color: ${p =>
    p.$status === 'completed' ? '#3b82f6' :
    p.$status === 'failed' ? '#ef4444' :
    p.$status === 'cancelled' ? '#eab308' :
    p.$status === 'running' ? '#22c55e' :
    'var(--text-muted)'};
`;
const StepDetailRow = styled.div`
  display: grid; grid-template-columns: 26px 24px 1fr 110px 120px 76px 110px 60px;
  gap: 5px; padding: 6px 8px; align-items: center; font-size: 11px;
  border-bottom: 1px solid rgba(255,255,255,.04);
  &:last-child { border-bottom: none; }
  &:hover { background: rgba(255,255,255,.02); }
`;
const StepErrorBox = styled.div`
  margin-top: 4px; padding: 6px 10px; border-radius: 6px;
  background: rgba(239,68,68,.08); border: 1px solid rgba(239,68,68,.2);
  font-family: 'JetBrains Mono', monospace; font-size: 10px;
  color: #ef4444; white-space: pre-wrap; word-break: break-all;
  max-height: 120px; overflow-y: auto;
`;
const StepProfilePic = styled.img`
  width: 18px; height: 18px; border-radius: 50%; object-fit: cover;
  border: 1px solid var(--border); flex-shrink: 0;
`;
const StepProfileInitial = styled.div`
  width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 8px; font-weight: 800; color: #fff;
  background: ${p => p.$bg || '#6366f1'};
`;
const ScriptGroupBadge = styled.span`
  font-size: 8px; font-weight: 700; padding: 1px 5px; border-radius: 3px;
  text-transform: uppercase; letter-spacing: .3px;
  background: ${p => p.$bg || 'var(--surface)'};
  color: ${p => p.$color || 'var(--text-muted)'};
`;

const ExecProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, var(--primary), #22c55e);
  width: ${p => p.$pct}%;
  transition: width .5s ease;
`;

/* ═══════════════════════════════════════════════
   CUSTOM BEZIER EDGE (n8n style)
   ═══════════════════════════════════════════════ */

function BezierEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style, markerEnd, animated, selected }) {
  const { setEdges } = useReactFlow();
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    curvature: 0.4,
  });

  const handleDelete = (e) => {
    e.stopPropagation();
    setEdges(eds => eds.filter(edge => edge.id !== id));
  };

  return (
    <>
      {/* Invisible wider path for easier click targeting */}
      <path d={edgePath} fill="none" stroke="transparent" strokeWidth={20} style={{ cursor: 'pointer' }} />
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          ...style,
          stroke: selected ? '#ef4444' : 'var(--primary)',
          strokeWidth: selected ? 3.5 : 2.5,
          filter: selected ? 'drop-shadow(0 0 4px rgba(239,68,68,.5))' : 'none',
        }}
        markerEnd={markerEnd}
        interactionWidth={20}
      />
      {selected && (
        <foreignObject x={labelX - 10} y={labelY - 10} width={20} height={20} style={{ overflow: 'visible' }}>
          <div onClick={handleDelete} title="Supprimer cette connexion" style={{
            width: 20, height: 20, borderRadius: '50%', background: '#ef4444', display: 'flex',
            alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff',
            fontSize: 12, fontWeight: 700, boxShadow: '0 2px 6px rgba(0,0,0,.3)'
          }}>✕</div>
        </foreignObject>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════ */

const registeredNodeTypes = getAllNodeTypes();

// MUST be defined outside component for stable reference (ReactFlow requirement)
const stableNodeTypes = { custom: CustomNode };

function WorkflowBuilderContent({ initialLoadId, onConsumeLoad }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [workflowName, setWorkflowName] = useState('Nouveau Workflow');
  const [workflowId, setWorkflowId] = useState(null);
  const [savedWorkflows, setSavedWorkflows] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [devices, setDevices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [deviceAccounts, setDeviceAccounts] = useState([]);
  const [, setRunningProcesses] = useState([]);
  const [executionStatus, setExecutionStatus] = useState(null);
  const [nodeTestOutputs, setNodeTestOutputs] = useState({}); // { nodeId: { ...output } }
  const [executionHistory, setExecutionHistory] = useState([]);
  const [showExecHistory, setShowExecHistory] = useState(true);
  const [expandedExecId, setExpandedExecId] = useState(null);
  
  const nodeIdCounter = useRef(0);
  const getId = useCallback(() => {
    nodeIdCounter.current += 1;
    return `node_${Date.now()}_${nodeIdCounter.current}`;
  }, []);

  // Refs for callbacks so nodeTypes stays stable (ReactFlow requirement)
  const onConfigRef = useRef(null);
  const onDeleteRef = useRef(null);
  const onRunNowRef = useRef(null);

  // Delete a specific node + its connected edges
  onDeleteRef.current = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
  }, [setNodes, setEdges]);

  // Open config modal for a node — uses functional setNodes to get latest nodes
  onConfigRef.current = useCallback((nodeId) => {
    setNodes((currentNodes) => {
      const node = currentNodes.find(n => n.id === nodeId);
      if (node) {
        setSelectedNode(node);
        setShowConfigModal(true);
      }
      return currentNodes; // no mutation
    });
  }, [setNodes]);

  // Run workflow immediately (like n8n "Test workflow" button)
  onRunNowRef.current = useCallback(async () => {
    if (nodes.length === 0) {
      alert('⚠️ Le workflow est vide');
      return;
    }
    if (!window.confirm('Exécuter le workflow maintenant ?')) return;
    try {
      const res = await axios.post('/api/workflow-builder/execute', {
        id: workflowId || null,
        name: workflowName || 'Run Now',
        nodes: nodes.map(n => ({ ...n, data: { type: n.data?.type, label: n.data?.label, config: n.data?.config || {} } })),
        edges: edges,
      });
      if (res.data.success) {
        alert('✅ Workflow lancé !');
      } else {
        alert('❌ ' + (res.data.error || 'Erreur'));
      }
    } catch (err) {
      console.error('Run now error:', err);
      alert('❌ ' + (err.response?.data?.error || err.message));
    }
  }, [nodes, edges, workflowId, workflowName]);

  // Save node config from modal
  const handleSaveNodeConfig = useCallback((nodeId, config) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return { ...node, data: { ...node.data, config, onConfig: (...a) => onConfigRef.current(...a), onDelete: (...a) => onDeleteRef.current(...a), onRunNow: () => onRunNowRef.current() } };
        }
        return node;
      })
    );
  }, [setNodes]);

  // Register custom edge types (bezier curves like n8n)
  const rfEdgeTypes = useMemo(() => ({
    bezier: BezierEdge,
  }), []);

  // Charger les devices, comptes et workflows au montage
  useEffect(() => {
    const loadData = async () => {
      try {
        const [devicesRes, accountsRes, workflowsRes, deviceAccountsRes] = await Promise.all([
          axios.get('/api/devices'),
          axios.get('/api/instagram-accounts'),
          axios.get('/api/workflow-builder'),
          axios.get('/api/workflow-builder/device-accounts').catch(() => ({ data: { deviceAccounts: [] } }))
        ]);
        setDevices(devicesRes.data.devices || devicesRes.data || []);
        setAccounts(accountsRes.data.accounts || accountsRes.data || []);
        const wfs = workflowsRes.data.workflows || [];
        setSavedWorkflows(wfs);
        setDeviceAccounts(deviceAccountsRes.data.deviceAccounts || []);
        // Determine which workflow to load
        if (initialLoadId === '__new__') {
          // Create new workflow: clear everything
          setNodes([]);
          setEdges([]);
          setWorkflowId(null);
          setWorkflowName('Nouveau Workflow');
          nodeIdCounter.current = 0;
          if (onConsumeLoad) onConsumeLoad();
        } else {
          const targetId = initialLoadId || (wfs.length > 0 ? wfs[0].id : null);
          if (targetId) {
            try {
              const r = await axios.get(`/api/workflow-builder/${targetId}`);
              const wf = r.data.workflow;
              setWorkflowId(wf.id);
              setWorkflowName(wf.name);

              let parsedNodes = typeof wf.nodes === 'string' ? JSON.parse(wf.nodes) : wf.nodes;
              let parsedEdges = typeof wf.edges === 'string' ? JSON.parse(wf.edges) : wf.edges;

              const LEGACY_SCRIPT_TYPES = ['instagram-interact', 'instagram-scrape', 'instagram-unfollow', 'instagram-dm', 'gmail'];
              parsedNodes = (parsedNodes || []).map(n => {
                if (n.data && LEGACY_SCRIPT_TYPES.includes(n.data.type)) {
                  n = { ...n, data: { ...n.data, type: 'device', config: { ...n.data.config, script_type: n.data.type } } };
                }
                return n;
              });

              parsedNodes = parsedNodes.map(n => {
                const def = registeredNodeTypes.find(nt => nt.type === n.data?.type);
                if (def) {
                  return { ...n, data: { ...n.data, icon: def.icon, color: def.color, onConfig: (...a) => onConfigRef.current(...a), onDelete: (...a) => onDeleteRef.current(...a), onRunNow: () => onRunNowRef.current() } };
                }
                return { ...n, data: { ...n.data, onConfig: (...a) => onConfigRef.current(...a), onDelete: (...a) => onDeleteRef.current(...a), onRunNow: () => onRunNowRef.current() } };
              });

              parsedEdges = (parsedEdges || []).map(e => ({
                ...e, type: 'bezier', animated: true,
                style: { stroke: 'var(--primary)', strokeWidth: 2.5 },
                markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--primary)', width: 18, height: 18 },
              }));

              setNodes(parsedNodes);
              setEdges(parsedEdges);

              const maxId = parsedNodes.reduce((max, n) => {
                const match = n.id?.match(/_(\d+)$/);
                return match ? Math.max(max, parseInt(match[1])) : max;
              }, 0);
              nodeIdCounter.current = maxId + 1;
              console.log('✅ Loaded workflow:', wf.name);
            } catch (loadErr) {
              console.error('Load workflow error:', loadErr);
            }
          }
          if (onConsumeLoad) onConsumeLoad();
        }
      } catch (error) {
        console.error('Erreur chargement données:', error);
      }
    };
    loadData();
    // Poll running processes for node indicators
    const loadProcs = async () => {
      try {
        const r = await axios.get('/api/processes/all');
        setRunningProcesses((r.data.processes || []).filter(p => p.status === 'running'));
      } catch (_) {}
    };
    loadProcs();
    const procIv = setInterval(loadProcs, 8000);
    return () => clearInterval(procIv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll execution status for the current workflow (stabilized to avoid unnecessary re-renders)
  const executionStatusRef = useRef(null);
  useEffect(() => {
    if (!workflowId) { setExecutionStatus(null); executionStatusRef.current = null; return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`/api/workflow-builder/executions/${workflowId}`);
        if (cancelled) return;
        const newExec = r.data.execution || null;
        // Only update state if something actually changed (prevents unnecessary re-renders)
        const prev = executionStatusRef.current;
        const changed = (!prev && newExec)
          || (prev && !newExec)
          || prev?.status !== newExec?.status
          || prev?.currentStepIndex !== newExec?.currentStepIndex
          || (prev?.steps || []).map(s => s.status).join(',') !== (newExec?.steps || []).map(s => s.status).join(',');
        if (changed) {
          executionStatusRef.current = newExec;
          setExecutionStatus(newExec);
        }
      } catch (_) {}
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [workflowId]);

  // Fetch execution history from DB
  useEffect(() => {
    if (!workflowId) { setExecutionHistory([]); return; }
    let cancelled = false;
    const fetchHistory = async () => {
      try {
        const r = await axios.get(`/api/workflow-builder/execution-history/${workflowId}?limit=10`);
        if (!cancelled && r.data.executions) setExecutionHistory(r.data.executions);
      } catch (_) {}
    };
    fetchHistory();
    const iv = setInterval(fetchHistory, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [workflowId]);

  // Stop running workflow
  const stopWorkflow = useCallback(async () => {
    if (!workflowId) return;
    try {
      await axios.post(`/api/workflow-builder/executions/${workflowId}/stop`);
      // Optimistic UI update: immediately show as cancelled
      setExecutionStatus(prev => prev ? { ...prev, status: 'cancelled', completedAt: new Date().toISOString(),
        steps: (prev.steps || []).map(s => (s.status === 'pending' || s.status === 'running') ? { ...s, status: 'cancelled' } : s)
      } : prev);
      // Refetch execution history after a short delay
      setTimeout(async () => {
        try {
          const r = await axios.get(`/api/workflow-builder/execution-history/${workflowId}?limit=10`);
          if (r.data.executions) setExecutionHistory(r.data.executions);
        } catch (_) {}
      }, 2000);
    } catch (err) {
      console.error('Stop error:', err);
    }
  }, [workflowId]);

  // Connect nodes with bezier edges
  const onConnect = useCallback(
    (params) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'bezier',
            animated: true,
            style: { stroke: 'var(--primary)', strokeWidth: 2.5 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: 'var(--primary)',
              width: 18,
              height: 18,
            },
          },
          eds
        )
      );
    },
    [setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/reactflow');
      if (!type) return;

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const nodeTypeInfo = registeredNodeTypes.find(nt => nt.type === type);
      if (!nodeTypeInfo) return;

      // Auto-populate config for restarter nodes: find the workflow's restarter device
      let autoConfig = {};
      if (type === 'restarter') {
        const schedulerNode = nodes.find(n => n.data?.type === 'scheduler');
        const wfLetter = schedulerNode?.data?.config?.workflow_letter || null;
        let candidates = deviceAccounts.length > 0 ? deviceAccounts : devices.map(d => ({
          device_id: d.device_id || d.id, device_name: d.device_name || d.name || d.device_id || d.id,
          is_restarter: d.is_restarter, workflow_letter: d.workflow_letter || d.workflow
        }));
        if (wfLetter) candidates = candidates.filter(d => d.workflow_letter && d.workflow_letter.toUpperCase() === wfLetter.toUpperCase());
        const restarter = candidates.find(d => d.is_restarter);
        if (restarter) {
          autoConfig = { device_id: restarter.device_id, _restarter_name: restarter.device_name };
        }
      }

      const newNode = {
        id: getId(),
        type: 'custom',
        position,
        data: {
          label: nodeTypeInfo.label,
          type: type,
          icon: nodeTypeInfo.icon,
          color: nodeTypeInfo.color,
          config: autoConfig,
          onConfig: (...a) => onConfigRef.current(...a),
          onDelete: (...a) => onDeleteRef.current(...a),
          onRunNow: () => onRunNowRef.current(),
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes, getId, nodes, deviceAccounts, devices]
  );

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  // Keyboard: Delete/Backspace to remove selected nodes/edges
  const onKeyDown = useCallback((event) => {
    if (event.key === 'Delete' || event.key === 'Backspace') {
      // React Flow handles this via onNodesChange/onEdgesChange with deleteElements
    }
  }, []);

  // Save workflow to DB
  const saveWorkflow = async () => {
    if (!reactFlowInstance) return;

    const flow = reactFlowInstance.toObject();

    if (nodes.length === 0) {
      alert('⚠️ Le workflow est vide');
      return;
    }

    const workflowData = {
      id: workflowId,
      name: workflowName,
      nodes: flow.nodes,
      edges: flow.edges,
      viewport: flow.viewport,
    };

    try {
      const response = await axios.post('/api/workflow-builder/save', workflowData);
      const saved = response.data.workflow;
      setWorkflowId(saved.id);
      alert('✅ Workflow sauvegardé !');

      const workflowsRes = await axios.get('/api/workflow-builder');
      setSavedWorkflows(workflowsRes.data.workflows || []);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('❌ Erreur: ' + (error.response?.data?.error || error.message));
    }
  };

  // Execute workflow
  const runWorkflow = async () => {
    if (nodes.length === 0) {
      alert('⚠️ Le workflow est vide');
      return;
    }

    // Build execution order from edges (BFS from root nodes)
    const executionOrder = buildExecutionOrder(nodes, edges);

    try {
      // Save first if not saved
      if (!workflowId && reactFlowInstance) {
        const flow = reactFlowInstance.toObject();
        const saveRes = await axios.post('/api/workflow-builder/save', {
          name: workflowName,
          nodes: flow.nodes,
          edges: flow.edges,
          viewport: flow.viewport,
        });
        setWorkflowId(saveRes.data.workflow.id);
      }

      const response = await axios.post('/api/workflow-builder/execute', {
        id: workflowId,
        name: workflowName,
        nodes,
        edges,
        executionOrder
      });
      alert('✅ Workflow lancé !');
      console.log('Workflow exécuté:', response.data);
    } catch (error) {
      console.error('Erreur exécution:', error);
      alert('❌ Erreur: ' + (error.response?.data?.error || error.message));
    }
  };

  // Build execution order following edges from root nodes
  const buildExecutionOrder = (allNodes, allEdges) => {
    const order = [];
    const visited = new Set();
    
    // Find root nodes (no incoming edges)
    const targetIds = new Set(allEdges.map(e => e.target));
    const roots = allNodes.filter(n => !targetIds.has(n.id));
    
    const traverse = (nodeId) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);
      const node = allNodes.find(n => n.id === nodeId);
      if (node) {
        order.push({
          id: node.id,
          type: node.data.type,
          label: node.data.label,
          config: node.data.config || {}
        });
      }
      const nextEdges = allEdges.filter(e => e.source === nodeId);
      nextEdges.forEach(edge => traverse(edge.target));
    };
    
    roots.forEach(root => traverse(root.id));
    // Add any unvisited nodes
    allNodes.forEach(n => { if (!visited.has(n.id)) traverse(n.id); });
    
    return order;
  };

  const clearWorkflow = () => {
    if (window.confirm('Effacer le workflow ?')) {
      setNodes([]);
      setEdges([]);
      setWorkflowId(null);
      setWorkflowName('Nouveau Workflow');
    }
  };

  const loadWorkflow = async (wfId) => {
    try {
      const response = await axios.get(`/api/workflow-builder/${wfId}`);
      const workflow = response.data.workflow;

      setWorkflowId(workflow.id);
      setWorkflowName(workflow.name);

      let parsedNodes = typeof workflow.nodes === 'string' ? JSON.parse(workflow.nodes) : workflow.nodes;
      let parsedEdges = typeof workflow.edges === 'string' ? JSON.parse(workflow.edges) : workflow.edges;

      // Migrate legacy node types (instagram-interact, gmail, etc.) → unified 'device' node
      const LEGACY_SCRIPT_TYPES = ['instagram-interact', 'instagram-scrape', 'instagram-unfollow', 'instagram-dm', 'gmail'];
      parsedNodes = (parsedNodes || []).map(n => {
        if (n.data && LEGACY_SCRIPT_TYPES.includes(n.data.type)) {
          const legacyType = n.data.type;
          n = { ...n, data: { ...n.data, type: 'device', config: { ...n.data.config, script_type: legacyType } } };
        }
        return n;
      });

      // Restore icon references from node registry (icons can't be serialized)
      parsedNodes = parsedNodes.map(n => {
        const def = registeredNodeTypes.find(nt => nt.type === n.data?.type);
        if (def) {
          return { ...n, data: { ...n.data, icon: def.icon, color: def.color, onConfig: (...a) => onConfigRef.current(...a), onDelete: (...a) => onDeleteRef.current(...a), onRunNow: () => onRunNowRef.current() } };
        }
        return { ...n, data: { ...n.data, onConfig: (...a) => onConfigRef.current(...a), onDelete: (...a) => onDeleteRef.current(...a), onRunNow: () => onRunNowRef.current() } };
      });

      // Ensure edges use bezier type
      parsedEdges = (parsedEdges || []).map(e => ({
        ...e,
        type: 'bezier',
        animated: true,
        style: { stroke: 'var(--primary)', strokeWidth: 2.5 },
        markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--primary)', width: 18, height: 18 },
      }));

      setNodes(parsedNodes);
      setEdges(parsedEdges);

      if (workflow.viewport && reactFlowInstance) {
        const viewport = typeof workflow.viewport === 'string' ? JSON.parse(workflow.viewport) : workflow.viewport;
        reactFlowInstance.setViewport(viewport);
      }

      // Update ID counter to avoid collisions
      const maxId = parsedNodes.reduce((max, n) => {
        const match = n.id?.match(/_(\d+)$/);
        return match ? Math.max(max, parseInt(match[1])) : max;
      }, 0);
      nodeIdCounter.current = maxId + 1;

      console.log('✅ Workflow chargé:', workflow.name, `(${parsedNodes.length} nodes, ${parsedEdges.length} edges)`);
    } catch (error) {
      console.error('Erreur chargement workflow:', error);
      alert('❌ Erreur lors du chargement');
    }
  };

  // Rename workflow on blur (persist name change to DB)
  const renameWorkflow = async () => {
    if (!workflowId || !workflowName.trim()) return;
    try {
      await axios.patch(`/api/workflow-builder/rename/${workflowId}`, { name: workflowName.trim() });
      const workflowsRes = await axios.get('/api/workflow-builder');
      setSavedWorkflows(workflowsRes.data.workflows || []);
    } catch (error) {
      console.error('Rename error:', error);
    }
  };

  const deleteWorkflow = async () => {
    if (!workflowId) return;
    if (!window.confirm('Supprimer ce workflow ?')) return;
    try {
      await axios.delete(`/api/workflow-builder/${workflowId}`);
      setWorkflowId(null);
      setWorkflowName('Nouveau Workflow');
      setNodes([]);
      setEdges([]);
      const workflowsRes = await axios.get('/api/workflow-builder');
      setSavedWorkflows(workflowsRes.data.workflows || []);
      alert('✅ Workflow supprimé');
    } catch (error) {
      alert('❌ Erreur: ' + error.message);
    }
  };

  // Compute which nodes have running scripts
  // Only use execution step-level status (no device-based fallback which caused false positives)
  const enrichedNodes = useMemo(() => {
    // If a workflow execution is actively running, highlight ONLY the current step's node
    if (executionStatus && executionStatus.status === 'running' && executionStatus.steps) {
      const stepStatusMap = {};
      executionStatus.steps.forEach(s => { stepStatusMap[s.id] = s.status; });
      return nodes.map(n => {
        const isRunning = stepStatusMap[n.id] === 'running';
        if (isRunning !== (n.data?._isRunning || false)) {
          return { ...n, data: { ...n.data, _isRunning: isRunning } };
        }
        return n;
      });
    }
    // No active execution → clear all glow
    return nodes.map(n => {
      if (n.data?._isRunning) {
        return { ...n, data: { ...n.data, _isRunning: false } };
      }
      return n;
    });
  }, [nodes, executionStatus]);

  return (
    <WorkflowContainer>
      <TopBar>
        <TopBarLeft>
          <Zap size={24} color="var(--primary)" />
          <WorkflowTitle
            value={workflowName}
            onChange={(e) => setWorkflowName(e.target.value)}
            onBlur={renameWorkflow}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
            placeholder="Nom du workflow"
          />
          {savedWorkflows.length > 0 && (
            <select
              style={{
                padding: '8px 12px',
                background: 'var(--surface)',
                border: '1px solid var(--card-border)',
                borderRadius: '6px',
                color: 'var(--text-secondary)',
                fontSize: '13px',
                cursor: 'pointer',
                marginLeft: '12px'
              }}
              onChange={(e) => {
                if (e.target.value) loadWorkflow(parseInt(e.target.value));
              }}
              value={workflowId || ''}
            >
              <option value="">Charger un workflow...</option>
              {savedWorkflows.map(wf => (
                <option key={wf.id} value={wf.id}>
                  {wf.name} ({new Date(wf.updated_at).toLocaleDateString()})
                </option>
              ))}
            </select>
          )}
        </TopBarLeft>
        <TopBarRight>
          {isAdmin && (
            <>
              <ActionButton onClick={saveWorkflow}>
                <Save size={18} /> Sauvegarder
              </ActionButton>
              <ActionButton onClick={clearWorkflow}>
                <Trash2 size={18} /> Effacer
              </ActionButton>
              {workflowId && (
                <ActionButton $danger onClick={deleteWorkflow}>
                  <X size={18} /> Supprimer
                </ActionButton>
              )}
            </>
          )}
          {executionStatus?.status === 'running' ? (
            <ActionButton $danger onClick={stopWorkflow}>
              <X size={18} /> Stopper
            </ActionButton>
          ) : (
            <ActionButton $primary onClick={runWorkflow}>
              <Play size={18} /> Exécuter
            </ActionButton>
          )}
        </TopBarRight>
      </TopBar>

      <FlowWrapper>
        <div ref={reactFlowWrapper} style={{ width: '100%', height: '100%' }} onKeyDown={isAdmin ? onKeyDown : undefined}>
          <ReactFlow
            nodes={enrichedNodes}
            edges={edges}
            onNodesChange={isAdmin ? onNodesChange : undefined}
            onEdgesChange={isAdmin ? onEdgesChange : undefined}
            onConnect={isAdmin ? onConnect : undefined}
            onInit={setReactFlowInstance}
            onDrop={isAdmin ? onDrop : undefined}
            onDragOver={isAdmin ? onDragOver : undefined}
            nodeTypes={stableNodeTypes}
            edgeTypes={rfEdgeTypes}
            defaultEdgeOptions={{
              type: 'bezier',
              animated: true,
              style: { stroke: 'var(--primary)', strokeWidth: 2.5 },
              markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--primary)', width: 18, height: 18 },
            }}
            fitView
            nodesDraggable={isAdmin}
            nodesConnectable={isAdmin}
            elementsSelectable={isAdmin}
            deleteKeyCode={isAdmin ? ['Backspace', 'Delete'] : null}
            edgesFocusable={isAdmin}
            edgesUpdatable={isAdmin}
            onEdgeDoubleClick={isAdmin ? (event, edge) => {
              event.stopPropagation();
              setEdges(eds => eds.filter(e => e.id !== edge.id));
            } : undefined}
            style={{ background: 'var(--bg)' }}
            connectionLineStyle={{ stroke: 'var(--primary)', strokeWidth: 2 }}
            connectionLineType="bezier"
          >
            <Background color="var(--card-border)" gap={20} size={1} />
            <Controls style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }} />
            <MiniMap
              style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
              nodeColor={(node) => node.data?.color || 'var(--primary)'}
              maskColor="rgba(0,0,0,.3)"
            />
          </ReactFlow>
        </div>

        {isAdmin && <SidebarPanel>
          <SidebarTitle>
            <Zap size={20} />
            Ajouter un noeud
          </SidebarTitle>
          <NodeTypesList>
            {/* Actions */}
            <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: 1, padding: '8px 0 4px', opacity: .7 }}>Actions</div>
            {registeredNodeTypes.filter(n => n.category === 'action').map((nodeType) => (
              <NodeTypeItem key={nodeType.type} $color={nodeType.color} draggable onDragStart={(e) => onDragStart(e, nodeType.type)}>
                <SidebarNodeIcon $color={nodeType.color}><nodeType.icon size={20} /></SidebarNodeIcon>
                <NodeInfo><NodeTypeName>{nodeType.label}</NodeTypeName><NodeTypeDesc>{nodeType.description}</NodeTypeDesc></NodeInfo>
              </NodeTypeItem>
            ))}
            {/* Logic */}
            <div style={{ fontSize: 10, fontWeight: 800, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 1, padding: '12px 0 4px', opacity: .7 }}>Logique</div>
            {registeredNodeTypes.filter(n => n.category === 'logic').map((nodeType) => (
              <NodeTypeItem key={nodeType.type} $color={nodeType.color} draggable onDragStart={(e) => onDragStart(e, nodeType.type)}>
                <SidebarNodeIcon $color={nodeType.color}><nodeType.icon size={20} /></SidebarNodeIcon>
                <NodeInfo><NodeTypeName>{nodeType.label}</NodeTypeName><NodeTypeDesc>{nodeType.description}</NodeTypeDesc></NodeInfo>
              </NodeTypeItem>
            ))}
            {/* Triggers */}
            <div style={{ fontSize: 10, fontWeight: 800, color: '#14b8a6', textTransform: 'uppercase', letterSpacing: 1, padding: '12px 0 4px', opacity: .7 }}>Déclencheur</div>
            {registeredNodeTypes.filter(n => n.category === 'trigger').map((nodeType) => (
              <NodeTypeItem key={nodeType.type} $color={nodeType.color} draggable onDragStart={(e) => onDragStart(e, nodeType.type)}>
                <SidebarNodeIcon $color={nodeType.color}><nodeType.icon size={20} /></SidebarNodeIcon>
                <NodeInfo><NodeTypeName>{nodeType.label}</NodeTypeName><NodeTypeDesc>{nodeType.description}</NodeTypeDesc></NodeInfo>
              </NodeTypeItem>
            ))}
          </NodeTypesList>
        </SidebarPanel>}

        {/* Execution Status Panel */}
        {executionStatus && (
          <ExecPanel>
            <ExecHeader>
              <ExecTitle>
                <Zap size={14} />
                Exécution
                <ExecStatusBadge $status={executionStatus.status}>
                  {executionStatus.status === 'running' ? '▶ En cours' :
                   executionStatus.status === 'completed' ? '✓ Terminé' :
                   executionStatus.status === 'completed_with_errors' ? '⚠ Erreurs' :
                   executionStatus.status === 'cancelled' ? '⏹ Annulé' :
                   executionStatus.status}
                </ExecStatusBadge>
              </ExecTitle>
              {executionStatus.status === 'running' && (
                <ExecStopBtn onClick={stopWorkflow}>
                  <X size={12} /> Stop
                </ExecStopBtn>
              )}
            </ExecHeader>
            <ExecProgress>
              <ExecProgressFill $pct={
                executionStatus.steps.length > 0
                  ? Math.round((executionStatus.steps.filter(s => s.status === 'completed' || s.status === 'failed').length / executionStatus.steps.length) * 100)
                  : 0
              } />
            </ExecProgress>
            <ExecStepList>
              {executionStatus.steps.map((step, i) => (
                <div key={i}>
                  <ExecStep $status={step.status}>
                    <ExecStepDot $status={step.status} />
                    <ExecStepLabel>{step.label}</ExecStepLabel>
                    <ExecStepTime>
                      {step.status === 'running' && step.startedAt
                        ? new Date(step.startedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                        : step.status === 'completed' && step.completedAt
                        ? new Date(step.completedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                        : step.status === 'failed' ? '✗' : ''}
                    </ExecStepTime>
                  </ExecStep>
                  {step.output && (step.status === 'completed' || step.status === 'failed') && (
                    <div style={{ marginLeft: 20, marginBottom: 4, padding: '3px 8px', background: 'var(--surface)', borderRadius: 4, fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace', maxHeight: 40, overflow: 'hidden', cursor: 'pointer' }}
                      title={JSON.stringify(step.output, null, 2)}
                      onClick={() => alert(JSON.stringify(step.output, null, 2))}
                    >
                      📤 {JSON.stringify(step.output).substring(0, 80)}{JSON.stringify(step.output).length > 80 ? '…' : ''}
                    </div>
                  )}
                </div>
              ))}
            </ExecStepList>
          </ExecPanel>
        )}
        {/* Bottom Execution History Panel — split layout: list | detail */}
        {workflowId && executionHistory.length > 0 && (
          <BottomHistoryPanel $collapsed={!showExecHistory}>
            <BottomHistoryHeader onClick={() => setShowExecHistory(!showExecHistory)}>
              <BottomHistoryTitle>
                <Clock size={14} />
                Historique d'exécution ({executionHistory.length})
              </BottomHistoryTitle>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{showExecHistory ? '▼ Réduire' : '▲ Développer'}</span>
            </BottomHistoryHeader>
            {showExecHistory && (() => {
              const selectedExec = executionHistory.find(e => e.id === expandedExecId);
              const selectedSteps = selectedExec ? (typeof selectedExec.steps === 'string' ? JSON.parse(selectedExec.steps) : (selectedExec.steps || [])) : [];
              const scriptTypeLabel = (st) => {
                if (!st || st === 'device') return '';
                const map = { 'interact': 'Interact', 'scrape': 'Scrape', 'unfollow': 'Unfollow', 'scrape_followers': 'Scrape Followers', 'scrape_following': 'Scrape Following', 'scrape_post_likers': 'Scrape Post Likers', 'interact-post-likers': 'Post Likers', 'interact-list': 'Interact Liste', 'like-comment': 'Like Comment', 'dm': 'DM', 'follower-analysis': 'Analyse Followers', 'detect': 'Détecter' };
                return map[st] || st.charAt(0).toUpperCase() + st.slice(1);
              };
              const getScriptGroup = (st) => {
                if (!st) return null;
                if (st.startsWith('instagram-') || ['interact','scrape','unfollow','dm','detect','follower-analysis','like-comment','interact-post-likers','interact-list'].includes(st)) return { label: 'Instagram', bg: 'rgba(225,48,108,.12)', color: '#e1306c' };
                if (st.startsWith('gmail') || st.startsWith('email')) return { label: 'Gmail', bg: 'rgba(234,67,53,.12)', color: '#ea4335' };
                if (st.startsWith('tiktok')) return { label: 'TikTok', bg: 'rgba(0,0,0,.12)', color: '#fff' };
                return null;
              };
              const fmtStepTime = (ts) => ts ? new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Paris' }) : '-';
              const fmtDur = (startedAt, completedAt) => {
                if (!startedAt || !completedAt) return '-';
                const ms = new Date(completedAt) - new Date(startedAt);
                const s = Math.floor(ms / 1000) % 60;
                const m = Math.floor(ms / 60000);
                const h = Math.floor(ms / 3600000);
                return h > 0 ? `${h}h${String(m % 60).padStart(2,'0')}m` : m > 0 ? `${m}m${String(s).padStart(2,'0')}s` : `${s}s`;
              };
              const renderProfilePic = (step) => {
                const picUrl = step.profilePicUrl || (step.username ? getLocalProfilePicUrl(step.username) : null);
                if (picUrl) return <StepProfilePic src={picUrl} alt="" onError={e => { e.target.style.display = 'none'; }} />;
                if (step.username) return <StepProfileInitial $bg="#6366f1">{step.username.charAt(0).toUpperCase()}</StepProfileInitial>;
                return <span style={{ width: 18 }} />;
              };
              const renderProfilePicInline = (step) => {
                const picUrl = step.profilePicUrl || (step.username ? getLocalProfilePicUrl(step.username) : null);
                if (picUrl) return <StepProfilePic src={picUrl} alt="" style={{ width: 20, height: 20 }} onError={e => { e.target.style.display = 'none'; }} />;
                if (step.username) return <StepProfileInitial $bg="#6366f1" style={{ width: 20, height: 20, fontSize: 9 }}>{step.username.charAt(0).toUpperCase()}</StepProfileInitial>;
                return null;
              };

              return (
                <HistorySplitLayout>
                  {/* Left: execution list */}
                  <HistoryListPane $hasDetail={!!selectedExec}>
                    <HistoryRow>
                      <span>#</span><span>Démarré</span><span>Durée</span><span>Status</span><span>Étapes</span>
                    </HistoryRow>
                    {executionHistory.map((exec) => {
                      const started = exec.started_at ? new Date(exec.started_at) : null;
                      const completed = exec.completed_at ? new Date(exec.completed_at) : null;
                      const duration = (started && completed) ? fmtDur(exec.started_at, exec.completed_at) : (exec.status === 'running' ? '...' : '-');
                      const steps = typeof exec.steps === 'string' ? JSON.parse(exec.steps) : (exec.steps || []);
                      const isActive = expandedExecId === exec.id;
                      const failedCount = steps.filter(s => s.status === 'failed').length;
                      return (
                        <HistoryRow key={exec.id} $active={isActive} onClick={() => setExpandedExecId(isActive ? null : exec.id)}>
                          <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace' }}>#{exec.id}</span>
                          <span style={{ fontFamily: 'monospace' }}>
                            {started ? started.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </span>
                          <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{duration}</span>
                          <ExecStatusBadge $status={exec.status}>
                            {exec.status === 'completed' ? '✓' : exec.status === 'completed_with_errors' ? '⚠' : exec.status === 'cancelled' ? '⏹' : exec.status === 'running' ? '▶' : '✗'}
                            {' '}{exec.status === 'completed_with_errors' ? 'ERREURS' : exec.status === 'running' ? 'EN COURS' : exec.status?.toUpperCase()}
                          </ExecStatusBadge>
                          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                            <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{steps.length} étape{steps.length > 1 ? 's' : ''}</span>
                            {failedCount > 0 && <span style={{ fontSize: 9, color: '#ef4444', fontWeight: 700 }}>{failedCount} err.</span>}
                          </div>
                        </HistoryRow>
                      );
                    })}
                  </HistoryListPane>

                  {/* Right: detail panel */}
                  {selectedExec && (
                    <HistoryDetailPane>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <List size={13} />
                          <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-primary)' }}>
                            Exécution #{selectedExec.id}
                          </span>
                          <ExecStatusBadge $status={selectedExec.status} style={{ fontSize: 9 }}>
                            {selectedExec.status === 'completed' ? '✓' : selectedExec.status === 'completed_with_errors' ? '⚠' : selectedExec.status === 'cancelled' ? '⏹' : selectedExec.status === 'running' ? '▶' : '✗'}
                            {' '}{selectedExec.status?.toUpperCase()}
                          </ExecStatusBadge>
                          <span style={{ fontSize: 9, fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                            {selectedExec.started_at ? fmtStepTime(selectedExec.started_at) : ''} → {selectedExec.completed_at ? fmtStepTime(selectedExec.completed_at) : '...'}
                          </span>
                          <span style={{ fontSize: 10, fontWeight: 700, fontFamily: 'monospace' }}>
                            {fmtDur(selectedExec.started_at, selectedExec.completed_at)}
                          </span>
                        </div>
                        <X size={14} style={{ cursor: 'pointer', color: 'var(--text-muted)' }} onClick={() => setExpandedExecId(null)} />
                      </div>

                      <div style={{ background: 'var(--card-bg)', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
                        {/* Header row */}
                        <StepDetailRow style={{ fontWeight: 700, color: 'var(--text-muted)', fontSize: 9, textTransform: 'uppercase', padding: '6px 8px', background: 'var(--surface)' }}>
                          <span style={{ textAlign: 'center' }}>#</span>
                          <span></span>
                          <span>Type / Script</span>
                          <span>Appareil</span>
                          <span>Compte</span>
                          <span>Status</span>
                          <span>Horaire</span>
                          <span>Durée</span>
                        </StepDetailRow>
                        {selectedSteps.map((step, si) => {
                          const st = scriptTypeLabel(step.scriptType);
                          const group = getScriptGroup(step.scriptType);
                          const typeDisplay = step.type === 'scheduler' ? 'Scheduler'
                            : step.type === 'pause' || step.type === 'delay' ? 'Pause'
                            : step.type === 'restarter' ? 'Restarter'
                            : step.type === 'condition' ? 'Condition'
                            : step.type === 'code' ? 'Code'
                            : step.type === 'device' ? (st || 'Script') : (step.label || step.type);
                          return (
                            <div key={si}>
                              <StepDetailRow>
                                <span style={{ textAlign: 'center', fontFamily: 'monospace', fontWeight: 700, color: 'var(--text-muted)', fontSize: 10 }}>{si + 1}</span>
                                {renderProfilePic(step)}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                    {group && <ScriptGroupBadge $bg={group.bg} $color={group.color}>{group.label}</ScriptGroupBadge>}
                                    <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 11 }}>{typeDisplay}</span>
                                  </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {step.deviceName ? (
                                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 10 }}>{step.deviceName}</span>
                                  ) : null}
                                  {step.deviceId && (
                                    <span style={{ fontSize: 8, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{step.deviceId}</span>
                                  )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                  {step.profilePicUrl || step.username ? renderProfilePicInline(step) : null}
                                  {step.username ? (
                                    <span style={{ fontWeight: 600, color: 'var(--primary)', fontSize: 11 }}>@{step.username}</span>
                                  ) : (
                                    <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>-</span>
                                  )}
                                </div>
                                <HistoryStepChip $status={step.status} style={{ width: 'fit-content', fontSize: 9 }}>
                                  {step.status === 'completed' ? '✓' : step.status === 'failed' ? '✗' : step.status === 'cancelled' ? '⏹' : step.status === 'running' ? '▶' : '○'}
                                  {' '}{step.status}
                                </HistoryStepChip>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600, color: 'var(--text-primary)' }}>
                                    {fmtStepTime(step.startedAt)}
                                  </span>
                                  {step.completedAt && (
                                    <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--text-muted)' }}>
                                      → {fmtStepTime(step.completedAt)}
                                    </span>
                                  )}
                                </div>
                                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 11 }}>{fmtDur(step.startedAt, step.completedAt)}</span>
                              </StepDetailRow>
                              {step.error && (
                                <div style={{ padding: '0 4px 4px 52px' }}>
                                  <StepErrorBox>
                                    <span style={{ fontWeight: 800 }}>Erreur:</span> {step.error}
                                    {step.output && step.output.data && (
                                      <div style={{ marginTop: 4, opacity: .8 }}>
                                        <span style={{ fontWeight: 800 }}>Détails:</span> {typeof step.output.data === 'string' ? step.output.data : JSON.stringify(step.output.data, null, 2)}
                                      </div>
                                    )}
                                  </StepErrorBox>
                                </div>
                              )}
                              {step.output && step.status === 'failed' && !step.error && (
                                <div style={{ padding: '0 4px 4px 52px' }}>
                                  <StepErrorBox>
                                    <span style={{ fontWeight: 800 }}>Output:</span> {typeof step.output === 'string' ? step.output : JSON.stringify(step.output, null, 2)}
                                  </StepErrorBox>
                                </div>
                              )}
                              {step.session && (
                                <div style={{ padding: '0 4px 4px 52px' }}>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 9 }}>
                                    {step.session.totalLikes > 0 && <span style={{ color: '#ef4444' }}>❤ {step.session.totalLikes}</span>}
                                    {step.session.totalFollows > 0 && <span style={{ color: '#22c55e' }}>👤+ {step.session.totalFollows}</span>}
                                    {step.session.totalUnfollows > 0 && <span style={{ color: '#f59e0b' }}>👤- {step.session.totalUnfollows}</span>}
                                    {step.session.totalGetProfile > 0 && <span style={{ color: '#a591ef' }}>👁 {step.session.totalGetProfile}</span>}
                                    {step.session.totalStoryViews > 0 && <span style={{ color: '#f59e0b' }}>📖 {step.session.totalStoryViews}</span>}
                                    {step.session.totalComments > 0 && <span style={{ color: '#3b82f6' }}>💬 {step.session.totalComments}</span>}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </HistoryDetailPane>
                  )}
                </HistorySplitLayout>
              );
            })()}
          </BottomHistoryPanel>
        )}
      </FlowWrapper>

      {showConfigModal && selectedNode && (
        <NodePropertiesPanel
          node={selectedNode}
          onClose={() => { setShowConfigModal(false); setSelectedNode(null); }}
          onSave={(nodeId, cfg) => {
            handleSaveNodeConfig(nodeId, cfg);
          }}
          devices={devices}
          accounts={accounts}
          deviceAccounts={deviceAccounts}
          schedulerWorkflowLetter={(() => {
            const schedulerNode = nodes.find(n => n.data?.type === 'scheduler');
            return schedulerNode?.data?.config?.workflow_letter || null;
          })()}
          previousNodeOutput={(() => {
            // Find the parent node connected to selectedNode and return its last test output
            const incomingEdge = edges.find(e => e.target === selectedNode.id);
            if (incomingEdge) {
              return nodeTestOutputs[incomingEdge.source] || null;
            }
            return null;
          })()}
          onTestOutput={(output) => {
            if (selectedNode) {
              setNodeTestOutputs(prev => ({ ...prev, [selectedNode.id]: output }));
            }
          }}
        />
      )}
    </WorkflowContainer>
  );
}

/* ═══════════════════════════════════════════════
   CUSTOM NODE COMPONENT
   ═══════════════════════════════════════════════ */

// Script type → icon + color mapping
const SCRIPT_ICON_MAP = {
  'instagram-interact': { icon: Heart, color: '#a855f7', bg: 'rgba(168,85,247,.12)', label: 'Interact', border: '#a855f7' },
  'instagram-scrape': { icon: Search, color: '#3b82f6', bg: 'rgba(59,130,246,.12)', label: 'Scrape', border: '#3b82f6' },
  'instagram-unfollow': { icon: UserMinus, color: '#f97316', bg: 'rgba(249,115,22,.12)', label: 'Unfollow', border: '#f97316' },
  'instagram-dm': { icon: Send, color: '#06b6d4', bg: 'rgba(6,182,212,.12)', label: 'DM', border: '#06b6d4' },
  'instagram-follower-analysis': { icon: Users, color: '#10b981', bg: 'rgba(16,185,129,.12)', label: 'Followers', border: '#10b981' },
  'instagram-detect': { icon: Eye, color: '#64748b', bg: 'rgba(100,116,139,.12)', label: 'Détecter', border: '#64748b' },
  'gmail-send': { icon: Mail, color: '#ea4335', bg: 'rgba(234,67,53,.12)', label: 'Gmail Send', border: '#ea4335' },
  'gmail-read': { icon: Mail, color: '#34a853', bg: 'rgba(52,168,83,.12)', label: 'Gmail Read', border: '#34a853' },
  'tiktok-interact': { icon: Eye, color: '#00f2ea', bg: 'rgba(0,242,234,.12)', label: 'TikTok', border: '#00f2ea' },
  'restart-connection': { icon: RefreshCw, color: '#f59e0b', bg: 'rgba(245,158,11,.12)', label: 'Restart', border: '#f59e0b' },
};

function CustomNode({ data, id }) {
  const Icon = data.icon || Zap;
  const isScheduler = data.type === 'scheduler';
  const isDevice = data.type === 'device';
  const isDelay = data.type === 'delay';
  const isRestarter = data.type === 'restarter';
  const isCode = data.type === 'code';
  const isCondition = data.type === 'condition';
  const hasConfig = data.config && Object.keys(data.config).length > 0;
  const c = data.config || {};
  const isRunning = data._isRunning || false;
  const canTest = isCode || isDelay || isCondition;

  // Script info for device nodes
  const scriptMeta = isDevice && c.script_type ? SCRIPT_ICON_MAP[c.script_type] : null;
  const ScriptIcon = scriptMeta?.icon || Smartphone;
  const isIgScript = c.script_type?.startsWith('instagram');
  const isGmailScript = c.script_type?.startsWith('gmail');

  // Build the node icon to reflect the script type
  const nodeIcon = isDevice && scriptMeta ? ScriptIcon : Icon;

  return (
    <CustomNodeContainer $color={scriptMeta?.color || data.color} $running={isRunning}
      $compact={isRestarter} $fixedWidth={isDevice}>
      <RunningBadge $visible={isRunning}><Play size={8} /> EN COURS</RunningBadge>
      {!isScheduler && (
        <Handle type="target" position={Position.Left}
          style={{ background: scriptMeta?.color || data.color || 'var(--primary)', width: 12, height: 12, border: '2px solid var(--card-bg)' }} />
      )}
      <NodeHeader>
        <NodeIconWrapper $color={scriptMeta?.color || data.color}>
          {isDevice && isIgScript ? <Instagram size={18} /> :
           isDevice && isGmailScript ? <Mail size={18} /> :
           React.createElement(nodeIcon, { size: isRestarter ? 16 : 18 })}
        </NodeIconWrapper>
        <NodeLabel style={isRestarter ? { fontSize: 12 } : undefined}>{data.label}</NodeLabel>
        <div style={{ display: 'flex', gap: '3px' }}>
          {isScheduler && (
            <NodeBtn onClick={(e) => { e.stopPropagation(); data.onRunNow && data.onRunNow(id); }}
              title="Exécuter le workflow" style={{ color: '#22c55e' }}>
              <Play size={14} />
            </NodeBtn>
          )}
          {canTest && (
            <NodeBtn onClick={(e) => { e.stopPropagation(); data.onConfig && data.onConfig(id); }}
              title="Configurer & Tester" style={{ color: '#22c55e' }}>
              <Play size={14} />
            </NodeBtn>
          )}
          <NodeBtn onClick={(e) => { e.stopPropagation(); data.onConfig && data.onConfig(id); }} title="Configurer">
            <Settings size={isRestarter ? 12 : 14} />
          </NodeBtn>
          <NodeBtn $danger onClick={(e) => { e.stopPropagation(); data.onDelete && data.onDelete(id); }} title="Supprimer">
            <X size={isRestarter ? 12 : 14} />
          </NodeBtn>
        </div>
      </NodeHeader>

      {/* ── Device node: redesigned ── */}
      {isDevice && (c._instagram_username || c.device_id) ? (
        <>
          <NodeInfoRow>
            {isIgScript && c._instagram_username ? (
              <NodeAvatar
                src={getLocalProfilePicUrl(c._instagram_username)}
                alt="" $color={scriptMeta?.color || '#a855f7'}
                onError={e => {
                  const step = parseInt(e.target.dataset.fb || '0');
                  if (step === 0) { e.target.dataset.fb = '1'; e.target.src = getProfilePicUrl(c._instagram_username); return; }
                  if (step === 1) { e.target.dataset.fb = '2'; e.target.src = `https://unavatar.io/instagram/${c._instagram_username}`; return; }
                  e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                }}
              />
            ) : null}
            {isIgScript && c._instagram_username ? (
              <NodeAvatarFallback style={{ display: 'none' }} $bg={scriptMeta?.bg} $color={scriptMeta?.color}>
                <Instagram size={16} />
              </NodeAvatarFallback>
            ) : isGmailScript ? (
              <NodeAvatarFallback $bg="rgba(234,67,53,.12)" $color="#ea4335">
                <Mail size={16} />
              </NodeAvatarFallback>
            ) : (
              <NodeAvatarFallback $bg="rgba(16,185,129,.12)" $color="#10b981">
                <Smartphone size={16} />
              </NodeAvatarFallback>
            )}
            <NodeInfoText>
              {c._instagram_username && <div className="ni-user">@{c._instagram_username}</div>}
              <div className="ni-device">{c._device_name || c.device_id?.substring(0, 16) || 'Device'}</div>
            </NodeInfoText>
          </NodeInfoRow>
          {scriptMeta && (
            <ScriptBadge $bg={scriptMeta.bg} $color={scriptMeta.color}>
              <ScriptIcon size={11} /> {scriptMeta.label}
            </ScriptBadge>
          )}
          {!c.script_type && (
            <NodeSubtext style={{ marginTop: 4 }}>
              <Settings size={10} /> <span style={{ color: 'var(--warning, #f59e0b)' }}>Script non configuré</span>
            </NodeSubtext>
          )}
        </>
      ) : isDevice ? (
        <NodeSubtext>
          <Smartphone size={10} /> <span style={{ color: 'var(--warning, #f59e0b)' }}>Device non sélectionné</span>
        </NodeSubtext>
      ) : null}

      {/* ── Scheduler node: schedule info ── */}
      {isScheduler && hasConfig && (
        <NodeSubtext>
          <Calendar size={10} />
          {c.schedule_type === 'cron' && c.schedule_cron && <span>Cron: <ConfigBadge>{c.schedule_cron}</ConfigBadge></span>}
          {c.schedule_type === 'datetime' && c.schedule_datetime && <span><ConfigBadge>{new Date(c.schedule_datetime).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</ConfigBadge></span>}
          {c.schedule_type === 'interval' && c.interval_minutes && <span>Toutes les <ConfigBadge>{c.interval_minutes}min</ConfigBadge></span>}
          {!c.schedule_type && <span style={{ color: 'var(--warning, #f59e0b)' }}>Non planifié</span>}
        </NodeSubtext>
      )}
      {isScheduler && !hasConfig && (
        <NodeSubtext><Calendar size={10} /> <span style={{ color: 'var(--warning, #f59e0b)' }}>Non configuré</span></NodeSubtext>
      )}

      {/* ── Delay node ── */}
      {isDelay && (
        <>
          <NodeSubtext>
            <Clock size={10} />
            {(c.delay_mode || 'random') === 'random'
              ? <span>🎲 <ConfigBadge>{c.delay_min || 30}s</ConfigBadge> — <ConfigBadge>{c.delay_max || 120}s</ConfigBadge> aléatoire</span>
              : c.delay_seconds
                ? <span><ConfigBadge>{c.delay_seconds}s</ConfigBadge> de pause</span>
                : <span style={{ color: '#6366f1', fontStyle: 'italic' }}>📥 delay_seconds du nœud précédent</span>
            }
          </NodeSubtext>
          <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
            📥 input · 📤 output
          </div>
        </>
      )}

      {/* ── Restarter node: compact ── */}
      {isRestarter && (
        c.device_id ? (
          <div style={{ marginTop: 4, fontSize: 10, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <RefreshCw size={10} color="#f59e0b" />
            <span style={{ fontWeight: 600, color: '#f59e0b' }}>{c._restarter_name || c.device_id?.substring(0, 12)}</span>
          </div>
        ) : (
          <NodeSubtext style={{ marginTop: 4 }}>
            <RefreshCw size={10} /> <span style={{ color: 'var(--warning, #f59e0b)', fontSize: 9 }}>Non configuré</span>
          </NodeSubtext>
        )
      )}

      {/* ── Code node ── */}
      {isCode && (
        <>
          <NodeSubtext>
            {c.language === 'python' ? '🐍 Python' : c.language === 'shell' ? '💻 Shell' : '📜 JavaScript'}
            {c.script ? <ConfigBadge>{c.script.length} chars</ConfigBadge> : <span style={{ color: 'var(--warning, #f59e0b)' }}> — vide</span>}
          </NodeSubtext>
          <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
            📥 input · 📤 result
          </div>
        </>
      )}

      {/* ── Condition node ── */}
      {isCondition && (
        <>
          <NodeSubtext>
            {hasConfig ? (
              <span>{c.condition_type} <ConfigBadge>{c.field}</ConfigBadge></span>
            ) : (
              <span style={{ color: 'var(--warning, #f59e0b)' }}>Non configuré</span>
            )}
          </NodeSubtext>
          <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
            📥 input · 📤 output
          </div>
        </>
      )}

      {/* ── Generic fallback ── */}
      {!isDevice && !isScheduler && !isDelay && !isRestarter && !isCode && !isCondition && (
        <NodeSubtext>
          {data.type}
          {hasConfig && <ConfigBadge>&#10003; Configuré</ConfigBadge>}
        </NodeSubtext>
      )}

      <Handle type="source" position={Position.Right}
        style={{ background: scriptMeta?.color || data.color || 'var(--primary)', width: 12, height: 12, border: '2px solid var(--card-bg)' }} />
    </CustomNodeContainer>
  );
}

/* ═══════════════════════════════════════════════
   CONTROL CENTER — Full Dashboard
   ═══════════════════════════════════════════════ */

const ccPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: .5; }
`;

const CCDashboard = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 0;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const CCSidebar = styled.div`
  background: var(--card-bg);
  border-right: 1px solid var(--card-border);
  padding: 20px 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CCMain = styled.div`
  padding: 20px 24px 40px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  & > * { flex-shrink: 0; }
`;

const CCSideSection = styled.div``;

const CCSideTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: var(--text-secondary);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CCSideCard = styled.div`
  padding: 10px 12px;
  background: ${p => p.$bg || 'var(--surface)'};
  border: 1px solid ${p => p.$border || 'var(--card-border)'};
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
  transition: all .15s;
  ${p => p.$clickable && `&:hover { border-color: var(--primary); transform: translateX(2px); }`}
`;

const CCSideLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CCSideMeta = styled.div`
  font-size: 10px;
  color: var(--text-secondary);
  margin-top: 3px;
`;

const CCKpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
`;

const CCKpiCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CCKpiIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$bg || 'rgba(99,102,241,.12)'};
  color: ${p => p.$color || '#6366f1'};
  margin-bottom: 4px;
`;

const CCKpiValue = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1;
`;

const CCKpiLabel = styled.div`
  font-size: 11px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const CCSection = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 10px;
  flex-shrink: 0;
`;

const CCSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--card-border);
  border-radius: 10px 10px 0 0;
`;

const CCSectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CCSectionCount = styled.span`
  background: var(--surface);
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
`;

const CCGrid = styled.div`
  display: flex;
  flex-direction: column;
`;

const ccCardRunningGlow = keyframes`
  0%, 100% { box-shadow: inset 0 0 0 1px rgba(245,158,11,.12), 0 0 6px rgba(245,158,11,.06); background: rgba(245,158,11,.02); }
  50% { box-shadow: inset 0 0 0 1px rgba(245,158,11,.32), 0 0 14px rgba(245,158,11,.12); background: rgba(245,158,11,.06); }
`;

const CCCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 18px;
  transition: all 0.12s;
  border-bottom: 1px solid var(--card-border);
  &:last-child { border-bottom: none; }
  &:hover { background: var(--surface); }
  ${p => p.$running && css`
    animation: ${ccCardRunningGlow} 2s ease-in-out infinite;
    border-left: 3px solid #f59e0b;
  `}
`;

const CCName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
`;

const CCMeta = styled.div`
  font-size: 10px;
  color: var(--text-secondary);
  margin-top: 2px;
  font-weight: 400;
`;

const CCBadge = styled.span`
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
  background: ${p =>
    p.$status === 'running' ? 'rgba(245,158,11,.18)' :
    p.$status === 'scheduled' ? 'rgba(34,197,94,.15)' :
    p.$status === 'disabled' ? 'rgba(239,68,68,.1)' :
    p.$status === 'completed' ? 'rgba(34,197,94,.15)' :
    p.$status === 'failed' ? 'rgba(239,68,68,.15)' :
    'rgba(100,116,139,.15)'};
  color: ${p =>
    p.$status === 'running' ? '#f59e0b' :
    p.$status === 'scheduled' ? '#22c55e' :
    p.$status === 'disabled' ? '#ef4444' :
    p.$status === 'completed' ? '#22c55e' :
    p.$status === 'failed' ? '#ef4444' :
    '#94a3b8'};
`;

const CCSteps = styled.div`
  display: flex;
  gap: 3px;
  align-items: center;
`;

const CCStepDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${p =>
    p.$s === 'completed' ? '#22c55e' :
    p.$s === 'running' ? '#f59e0b' :
    p.$s === 'failed' ? '#ef4444' :
    'rgba(100,116,139,.3)'};
  ${p => p.$s === 'running' && css`animation: ${ccPulse} 1.5s ease-in-out infinite;`}
`;

const CCRunningCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  background: rgba(245,158,11,.04);
  border-bottom: 1px solid rgba(245,158,11,.12);
  &:last-child { border-bottom: none; }
  cursor: pointer;
  &:hover { background: rgba(245,158,11,.08); }
`;

const CCRunningPulse = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #f59e0b;
  animation: ${ccPulse} 1.5s ease-in-out infinite;
  flex-shrink: 0;
`;

const ccRunningGlow = keyframes`
  0%, 100% { box-shadow: inset 0 0 0 1px rgba(245,158,11,.15), 0 0 8px rgba(245,158,11,.08); background: rgba(245,158,11,.04); }
  50% { box-shadow: inset 0 0 0 1px rgba(245,158,11,.35), 0 0 16px rgba(245,158,11,.15); background: rgba(245,158,11,.08); }
`;
const ccPausedGlow = keyframes`
  0%, 100% { box-shadow: inset 0 0 0 1px rgba(99,102,241,.15), 0 0 8px rgba(99,102,241,.08); background: rgba(99,102,241,.04); }
  50% { box-shadow: inset 0 0 0 1px rgba(99,102,241,.35), 0 0 16px rgba(99,102,241,.15); background: rgba(99,102,241,.08); }
`;

const CCHistoryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 18px;
  border-bottom: 1px solid var(--card-border);
  font-size: 12px;
  &:last-child { border-bottom: none; }
  &:hover { background: var(--surface); }
  ${p => p.$running && css`animation: ${ccRunningGlow} 2s ease-in-out infinite; border-left: 3px solid #f59e0b;`}
  ${p => p.$paused && css`animation: ${ccPausedGlow} 2.5s ease-in-out infinite; border-left: 3px solid #6366f1;`}
`;

const CCChartWrap = styled.div`
  padding: 16px 18px;
  height: 160px;
`;

const CCTooltipBox = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 11px;
  color: var(--text-primary);
  box-shadow: 0 4px 16px rgba(0,0,0,.25);
`;

function WorkflowControlCenter({ onOpenWorkflow, onCreateNew, onSelectDevice, setMirrorOpen }) {
  const [workflows, setWorkflows] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [history, setHistory] = useState([]);
  const [profilesAnalyzedToday, setProfilesAnalyzedToday] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [expandedExecId, setExpandedExecId] = useState(null);
  const [runningProcesses, setRunningProcesses] = useState([]);
  const [restartHistory, setRestartHistory] = useState([]);
  const renameRef = useRef(null);

  const toLocalDateStr = (dateInput) => {
    const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };

  const fetchData = useCallback(async () => {
    try {
      const [wfRes, exRes, histRes, profRes, procRes, restartRes] = await Promise.all([
        axios.get('/api/workflow-builder?all=1'),
        axios.get('/api/workflow-builder/executions'),
        axios.get('/api/workflow-builder/execution-history?limit=100'),
        axios.get('/api/workflow-builder/profiles-today').catch(() => ({ data: { count: 0 } })),
        axios.get('/api/processes').catch(() => ({ data: [] })),
        axios.get('/api/restart-history?limit=20').catch(() => ({ data: { history: [] } }))
      ]);
      setWorkflows(wfRes.data.workflows || []);
      setExecutions(exRes.data.executions || []);
      setHistory(histRes.data.executions || []);
      setProfilesAnalyzedToday(profRes.data.count || 0);
      setRunningProcesses(Array.isArray(procRes.data) ? procRes.data.filter(p => p.status === 'running') : []);
      setRestartHistory(restartRes.data?.history || []);
    } catch (e) {
      console.error('CC fetch error:', e);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 8000);
    return () => clearInterval(iv);
  }, [fetchData]);

  useEffect(() => {
    if (editingId && renameRef.current) renameRef.current.focus();
  }, [editingId]);

  // ─── Derived data ─────────────────────────────────────────
  const getStatus = (wf) => {
    if (!wf.is_active) return 'disabled';
    const exec = executions.find(e => String(e.workflowId) === String(wf.id));
    if (exec?.status === 'running') return 'running';
    try {
      const nodes = typeof wf.nodes === 'string' ? JSON.parse(wf.nodes) : (wf.nodes || []);
      const schedulerNode = nodes.find(n => (n.data?.type || n.type) === 'scheduler');
      if (schedulerNode) {
        const cfg = schedulerNode.data?.config || schedulerNode.config || {};
        if (cfg.schedule_type && cfg.schedule_type !== 'manual') return 'scheduled';
      }
    } catch (_) {}
    return 'inactive';
  };

  const getExecution = (wf) => executions.find(e => String(e.workflowId) === String(wf.id));

  // Get schedule description from scheduler node config
  const getScheduleInfo = (wf) => {
    try {
      const nodes = typeof wf.nodes === 'string' ? JSON.parse(wf.nodes) : (wf.nodes || []);
      const schedulerNode = nodes.find(n => (n.data?.type || n.type) === 'scheduler');
      if (!schedulerNode) return null;
      const cfg = schedulerNode.data?.config || schedulerNode.config || {};
      if (!cfg.schedule_type || cfg.schedule_type === 'manual') return null;
      if (cfg.schedule_type === 'cron' && cfg.schedule_cron) {
        // Parse common cron patterns into human-readable
        const parts = cfg.schedule_cron.split(' ');
        if (parts.length === 5) {
          const [min, hour] = parts;
          if (hour !== '*' && min !== '*') {
            return `Tous les jours à ${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
          }
        }
        return `Cron: ${cfg.schedule_cron}`;
      }
      if (cfg.schedule_type === 'interval' && cfg.interval_minutes) {
        const mins = parseInt(cfg.interval_minutes);
        if (mins >= 60) return `Toutes les ${Math.floor(mins / 60)}h${mins % 60 > 0 ? `${mins % 60}m` : ''}`;
        return `Toutes les ${mins}min`;
      }
      if (cfg.schedule_type === 'datetime' && cfg.schedule_datetime) {
        return new Date(cfg.schedule_datetime).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
      }
      return null;
    } catch (_) { return null; }
  };

  // Get last execution end time from history
  const getLastExecutionEnd = (wf) => {
    const wfHistory = history.filter(h => String(h.workflow_id) === String(wf.id) && h.completed_at);
    if (wfHistory.length === 0) return null;
    // history is already sorted by started_at DESC
    return wfHistory[0].completed_at;
  };

  const nodeCount = (wf) => {
    try {
      const n = typeof wf.nodes === 'string' ? JSON.parse(wf.nodes) : (wf.nodes || []);
      return n.length;
    } catch { return 0; }
  };

  const getNodes = (wf) => {
    try { return typeof wf.nodes === 'string' ? JSON.parse(wf.nodes) : (wf.nodes || []); }
    catch { return []; }
  };

  // KPI stats
  const todayStr = toLocalDateStr(new Date());
  const historyToday = history.filter(h => h.started_at && toLocalDateStr(h.started_at) === todayStr);
  const completedToday = historyToday.filter(h => h.status === 'completed' || h.status === 'completed_with_errors').length;
  const failedToday = historyToday.filter(h => h.status === 'failed').length;
  const runningNow = executions.filter(e => e.status === 'running' || e.status === 'paused');
  const totalHoursToday = historyToday.reduce((sum, h) => {
    if (h.started_at && h.completed_at) {
      return sum + (new Date(h.completed_at) - new Date(h.started_at)) / 3600000;
    }
    return sum;
  }, 0);

  // Chart: executions per day (last 7 days) — use local dates to avoid UTC offset issues
  const chartData = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i); d.setHours(12,0,0,0);
      const ds = toLocalDateStr(d);
      const dayLabel = d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });
      const dayExecs = history.filter(h => h.started_at && toLocalDateStr(h.started_at) === ds);
      const ok = dayExecs.filter(h => h.status === 'completed' || h.status === 'completed_with_errors').length;
      const fail = dayExecs.filter(h => h.status === 'failed').length;
      days.push({ name: dayLabel, ok, fail, total: ok + fail });
    }
    return days;
  }, [history]);

  // Restarter nodes across all workflows — deduplicated by device_id
  const restarters = useMemo(() => {
    const map = {};
    workflows.forEach(wf => {
      const nodes = getNodes(wf);
      nodes.forEach(n => {
        const type = n.data?.type || n.type;
        if (type === 'restarter') {
          const cfg = n.data?.config || {};
          const key = cfg.device_id || n.id;
          if (!map[key]) {
            map[key] = {
              workflowId: wf.id,
              workflowName: wf.name || `Workflow #${wf.id}`,
              nodeId: n.id,
              label: n.data?.label || 'Restarter',
              deviceId: cfg.device_id,
              deviceName: cfg._restarter_name || cfg.device_id?.substring(0,16) || '—',
              waitAfter: cfg.wait_after,
              workflows: [wf.name || `Workflow #${wf.id}`]
            };
          } else {
            const wfName = wf.name || `Workflow #${wf.id}`;
            if (!map[key].workflows.includes(wfName)) map[key].workflows.push(wfName);
          }
        }
      });
    });
    return Object.values(map);
  }, [workflows]);

  // Execution time per workflow (from history)
  const wfTimeStats = useMemo(() => {
    const map = {};
    history.forEach(h => {
      if (!h.started_at || !h.completed_at) return;
      const key = h.workflow_name || `Workflow #${h.workflow_id}`;
      if (!map[key]) map[key] = { name: key, totalMs: 0, count: 0 };
      map[key].totalMs += new Date(h.completed_at) - new Date(h.started_at);
      map[key].count++;
    });
    return Object.values(map).sort((a,b) => b.totalMs - a.totalMs).slice(0, 8);
  }, [history]);

  // ─── Actions ──────────────────────────────────────────────
  const handleToggle = async (e, wfId) => {
    e.stopPropagation();
    try { await axios.patch(`/api/workflow-builder/toggle/${wfId}`); fetchData(); }
    catch (err) { console.error('Toggle error:', err); }
  };

  const handleDelete = async (e, wfId, wfName) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer définitivement "${wfName}" ?`)) return;
    try { await axios.delete(`/api/workflow-builder/${wfId}`); fetchData(); }
    catch (err) { console.error('Delete error:', err); }
  };

  const startRename = (e, wf) => {
    e.stopPropagation();
    setEditingId(wf.id);
    setEditName(wf.name || '');
  };

  const commitRename = async (wfId) => {
    if (!editName.trim()) { setEditingId(null); return; }
    try { await axios.patch(`/api/workflow-builder/rename/${wfId}`, { name: editName.trim() }); setEditingId(null); fetchData(); }
    catch (err) { console.error('Rename error:', err); }
  };

  const [restartingDevice, setRestartingDevice] = useState(null);
  const handleRestart = async (e, deviceId, deviceName) => {
    e.stopPropagation();
    if (restartingDevice) return;
    setRestartingDevice(deviceId);
    try {
      // Log restart to history
      axios.post('/api/restart-history', { device_id: deviceId, device_name: deviceName, status: 'running' }).catch(() => {});
      await axios.post('/api/phone-cast/script', { device_id: deviceId, script: 'restart-connection' });
      console.log(`🔄 Restart lancé pour ${deviceName} (${deviceId})`);
    } catch (err) {
      console.error('Restart error:', err);
      alert(`Erreur restart: ${err.response?.data?.error || err.message}`);
    } finally {
      setTimeout(() => { setRestartingDevice(null); fetchData(); }, 5000);
    }
  };

  // ─── Workflow execution controls ───
  const handleStopWorkflow = async (e, workflowId) => {
    e.stopPropagation();
    if (!window.confirm('Arrêter ce workflow ?')) return;
    try {
      await axios.post(`/api/workflow-builder/executions/${workflowId}/stop`);
      fetchData();
    } catch (err) { console.error('Stop error:', err); }
  };
  const handlePauseWorkflow = async (e, workflowId) => {
    e.stopPropagation();
    try {
      await axios.post(`/api/workflow-builder/executions/${workflowId}/pause`);
      fetchData();
    } catch (err) { console.error('Pause error:', err); }
  };
  const handleResumeWorkflow = async (e, workflowId) => {
    e.stopPropagation();
    try {
      await axios.post(`/api/workflow-builder/executions/${workflowId}/resume`);
      fetchData();
    } catch (err) { console.error('Resume error:', err); }
  };

  const formatDuration = (ms) => {
    if (ms < 60000) return `${Math.round(ms/1000)}s`;
    if (ms < 3600000) return `${Math.round(ms/60000)}min`;
    const h = Math.floor(ms/3600000);
    const m = Math.round((ms % 3600000)/60000);
    return `${h}h${m > 0 ? `${m}m` : ''}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <CCTooltipBox>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
        <div style={{ color: '#22c55e' }}>{payload[0]?.value || 0} réussi(s)</div>
        {payload[1]?.value > 0 && <div style={{ color: '#ef4444' }}>{payload[1].value} échoué(s)</div>}
      </CCTooltipBox>
    );
  };

  return (
    <CCDashboard>
      {/* ═══ LEFT SIDEBAR ═══ */}
      <CCSidebar>
        {/* Running Now */}
        <CCSideSection>
          <CCSideTitle><Activity size={12} /> En cours</CCSideTitle>
          {runningNow.length === 0 ? (
            <CCSideMeta style={{ padding: '8px 0', fontStyle: 'italic' }}>Aucun workflow en cours</CCSideMeta>
          ) : runningNow.map((exec, i) => {
            const wf = workflows.find(w => String(w.id) === String(exec.workflowId));
            return (
              <CCSideCard key={i} $bg="rgba(245,158,11,.06)" $border="rgba(245,158,11,.2)" $clickable
                onClick={() => onOpenWorkflow && onOpenWorkflow(exec.workflowId)}>
                <CCSideLabel>
                  <CCRunningPulse />
                  {wf?.name || `Workflow #${exec.workflowId}`}
                </CCSideLabel>
                {exec.steps && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 3, alignItems: 'center' }}>
                    {exec.steps.map((s, j) => <CCStepDot key={j} $s={s.status} />)}
                    <span style={{ fontSize: 10, color: 'var(--text-secondary)', marginLeft: 4 }}>
                      {exec.steps.filter(s => s.status === 'completed').length}/{exec.steps.length}
                    </span>
                  </div>
                )}
                {exec.currentStep && (
                  <CCSideMeta>{exec.currentStep}</CCSideMeta>
                )}
              </CCSideCard>
            );
          })}
        </CCSideSection>

        {/* Restarters */}
        <CCSideSection>
          <CCSideTitle><RefreshCw size={12} /> Restarters ({restarters.length})</CCSideTitle>
          {restarters.length === 0 ? (
            <CCSideMeta style={{ padding: '8px 0', fontStyle: 'italic' }}>Aucun restarter configuré</CCSideMeta>
          ) : restarters.map((r, i) => (
            <CCSideCard key={r.deviceId || i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CCSideLabel>
                  <RefreshCw size={12} color="#f59e0b" />
                  {r.deviceName}
                </CCSideLabel>
                <CCSideMeta>
                  {r.workflows.length > 1 ? `${r.workflows.length} workflows` : r.workflows[0]}{r.waitAfter ? ` • ${r.waitAfter}s pause` : ''}
                </CCSideMeta>
              </div>
              {r.deviceId && (
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  {onSelectDevice && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onSelectDevice({ device_id: r.deviceId, device_name: r.deviceName, isConnected: true }); if (setMirrorOpen) setMirrorOpen(true); }}
                      title="Screen Mirror"
                      style={{ background: 'rgba(165,145,239,.12)', border: '1px solid rgba(165,145,239,.3)', borderRadius: 6, padding: '4px 6px', color: 'var(--primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', fontSize: 10, fontWeight: 700 }}
                    >
                      <Cast size={11} />
                    </button>
                  )}
                  <button
                    onClick={(e) => handleRestart(e, r.deviceId, r.deviceName)}
                    disabled={restartingDevice === r.deviceId}
                    title="Restart connexion"
                    style={{
                      background: restartingDevice === r.deviceId ? 'rgba(245,158,11,.3)' : 'rgba(245,158,11,.12)',
                      border: '1px solid rgba(245,158,11,.3)', borderRadius: 6, padding: '4px 8px',
                      color: '#f59e0b', cursor: restartingDevice === r.deviceId ? 'wait' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, flexShrink: 0,
                      transition: 'all .15s'
                    }}>
                    <RefreshCw size={11} style={{ animation: restartingDevice === r.deviceId ? 'spin 1s linear infinite' : 'none' }} />
                    {restartingDevice === r.deviceId ? '...' : 'Restart'}
                  </button>
                </div>
              )}
            </CCSideCard>
          ))}
        </CCSideSection>

        {/* Time per workflow */}
        {wfTimeStats.length > 0 && (
          <CCSideSection>
            <CCSideTitle><Timer size={12} /> Temps par workflow</CCSideTitle>
            {wfTimeStats.map((wt, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--card-border)' }}>
                <span style={{ fontSize: 11, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{wt.name}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', whiteSpace: 'nowrap' }}>{formatDuration(wt.totalMs)}</span>
              </div>
            ))}
          </CCSideSection>
        )}
      </CCSidebar>

      {/* ═══ MAIN CONTENT ═══ */}
      <CCMain>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ color: 'var(--text-primary)', fontSize: 20, fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Zap size={22} color="var(--primary)" /> Tableau de Bord
            </h2>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>Tableau de bord temps réel des workflows</div>
          </div>
          <button onClick={onCreateNew} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
            background: 'linear-gradient(135deg, var(--primary), var(--secondary))', border: 'none',
            borderRadius: 10, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
            transition: 'all .15s', boxShadow: '0 4px 12px rgba(0,0,0,.15)'
          }}>
            <Plus size={16} /> Nouveau workflow
          </button>
        </div>

        {/* KPI Row */}
        <CCKpiRow>
          <CCKpiCard>
            <CCKpiIcon $bg="rgba(34,197,94,.12)" $color="#22c55e"><CheckCircle2 size={18} /></CCKpiIcon>
            <CCKpiValue>{completedToday}</CCKpiValue>
            <CCKpiLabel>Exécutés aujourd'hui</CCKpiLabel>
          </CCKpiCard>
          <CCKpiCard>
            <CCKpiIcon $bg="rgba(245,158,11,.12)" $color="#f59e0b"><Activity size={18} /></CCKpiIcon>
            <CCKpiValue>{runningNow.length || restarters.length || 0}</CCKpiValue>
            <CCKpiLabel>En cours maintenant</CCKpiLabel>
          </CCKpiCard>
          <CCKpiCard>
            <CCKpiIcon $bg="rgba(168,85,247,.12)" $color="#a855f7"><Users size={18} /></CCKpiIcon>
            <CCKpiValue>{profilesAnalyzedToday}</CCKpiValue>
            <CCKpiLabel>Profils analysés</CCKpiLabel>
          </CCKpiCard>
          <CCKpiCard>
            <CCKpiIcon $bg="rgba(99,102,241,.12)" $color="#6366f1"><Timer size={18} /></CCKpiIcon>
            <CCKpiValue>{totalHoursToday.toFixed(1)}h</CCKpiValue>
            <CCKpiLabel>Heures aujourd'hui</CCKpiLabel>
          </CCKpiCard>
          <CCKpiCard>
            <CCKpiIcon $bg="rgba(239,68,68,.12)" $color="#ef4444"><XCircle size={18} /></CCKpiIcon>
            <CCKpiValue>{failedToday}</CCKpiValue>
            <CCKpiLabel>Erreurs aujourd'hui</CCKpiLabel>
          </CCKpiCard>
        </CCKpiRow>

        {/* Running Processes */}
        {(runningProcesses.length > 0 || restartHistory.length > 0) && (
          <CCSection>
            <CCSectionHeader>
              <CCSectionTitle><Monitor size={15} color="#0ea5e9" /> Processus en cours</CCSectionTitle>
              <CCSectionCount>{runningProcesses.length}</CCSectionCount>
            </CCSectionHeader>
            {runningProcesses.length === 0 ? (
              <div style={{ padding: '12px 20px', fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>Aucun processus actif</div>
            ) : runningProcesses.map((proc, i) => (
              <div key={proc.pid || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0, animation: 'pulse 2s infinite' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
                    {proc.script_type === 'restart-connection' ? '🔄 Restart connexion' : proc.script_type || proc.type || 'Script'}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                    {proc.device_name || proc.device_id || '—'}{proc.instagram_username ? ` · @${proc.instagram_username}` : ''}{proc.workstation_name ? ` · ${proc.workstation_name}` : ''}
                  </div>
                </div>
                {proc.started_at && (
                  <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: 'var(--text-muted)', fontWeight: 600 }}>
                    {new Date(proc.started_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            ))}
            {/* Restart history mini-list */}
            {restartHistory.length > 0 && (
              <div style={{ borderTop: '1px solid var(--border)' }}>
                <div style={{ padding: '8px 20px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                  Historique restarts récents
                </div>
                {restartHistory.slice(0, 5).map((rh, i) => (
                  <div key={rh.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 20px', fontSize: 11, borderBottom: '1px solid var(--border)' }}>
                    <RefreshCw size={10} style={{ color: rh.status === 'success' ? '#22c55e' : rh.status === 'error' ? '#ef4444' : '#f59e0b', flexShrink: 0 }} />
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{rh.device_name || rh.device_id || '—'}</span>
                    <span style={{ color: 'var(--text-muted)', flex: 1 }}>
                      {rh.ip_before && rh.ip_after ? `${rh.ip_before} → ${rh.ip_after}` : rh.ip_after || ''}
                    </span>
                    <span style={{ fontSize: 10, color: rh.status === 'success' ? '#22c55e' : rh.status === 'error' ? '#ef4444' : 'var(--text-muted)', fontWeight: 700 }}>
                      {rh.status === 'success' ? '✓' : rh.status === 'error' ? '✗' : '●'}
                    </span>
                    <span style={{ fontSize: 9, fontFamily: "'JetBrains Mono', monospace", color: 'var(--text-muted)' }}>
                      {rh.created_at ? new Date(rh.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CCSection>
        )}

        {/* Running Workflows (live) */}
        {runningNow.length > 0 && (
          <CCSection>
            <CCSectionHeader>
              <CCSectionTitle><Play size={15} color="#f59e0b" /> Workflows en cours</CCSectionTitle>
              <CCSectionCount>{runningNow.length}</CCSectionCount>
            </CCSectionHeader>
            {runningNow.map((exec, i) => {
              const wf = workflows.find(w => String(w.id) === String(exec.workflowId));
              const elapsed = exec.startedAt ? Date.now() - new Date(exec.startedAt).getTime() : 0;
              return (
                <CCRunningCard key={i} onClick={() => onOpenWorkflow && onOpenWorkflow(exec.workflowId)}>
                  <CCRunningPulse />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <CCName>{wf?.name || `Workflow #${exec.workflowId}`}</CCName>
                    <CCMeta>
                      {elapsed > 0 && `Depuis ${formatDuration(elapsed)}`}
                      {exec.currentStep && ` • Étape: ${exec.currentStep}`}
                      {wf?.workstation_name && <> • <span style={{ color: 'var(--primary)' }}>{wf.workstation_name}</span></>}
                    </CCMeta>
                  </div>
                  {exec.steps && (
                    <CCSteps title={`${exec.steps.filter(s => s.status === 'completed').length}/${exec.steps.length}`}>
                      {exec.steps.map((s, j) => <CCStepDot key={j} $s={s.status} />)}
                    </CCSteps>
                  )}
                  <CCBadge $status="running">▶ En cours</CCBadge>
                </CCRunningCard>
              );
            })}
          </CCSection>
        )}

        {/* Workflow List */}
        <CCSection>
          <CCSectionHeader>
            <CCSectionTitle><Zap size={15} /> Workflows</CCSectionTitle>
            <CCSectionCount>{workflows.length}</CCSectionCount>
          </CCSectionHeader>
          {workflows.length === 0 ? (
            <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '40px 0', fontSize: 13 }}>
              Aucun workflow
            </div>
          ) : (
            <CCGrid>
              {workflows.map(wf => {
                const status = getStatus(wf);
                const exec = getExecution(wf);
                const isEditing = editingId === wf.id;
                const disabled = !wf.is_active;
                const scheduleInfo = getScheduleInfo(wf);
                const lastExecEnd = getLastExecutionEnd(wf);
                return (
                  <CCCard key={wf.id} $running={status === 'running'} onClick={() => !isEditing && onOpenWorkflow && onOpenWorkflow(wf.id)}
                    style={{ cursor: isEditing ? 'default' : 'pointer', opacity: disabled ? .5 : 1 }}>
                    <button onClick={(e) => handleToggle(e, wf.id)} title={disabled ? 'Activer' : 'Désactiver'}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0 }}>
                      {disabled ? <ToggleLeft size={20} color="#94a3b8" /> : <ToggleRight size={20} color="#22c55e" />}
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isEditing ? (
                        <input ref={renameRef} value={editName} onChange={e => setEditName(e.target.value)}
                          onBlur={() => commitRename(wf.id)}
                          onKeyDown={e => { if (e.key === 'Enter') commitRename(wf.id); if (e.key === 'Escape') setEditingId(null); }}
                          onClick={e => e.stopPropagation()}
                          style={{ width: '100%', padding: '3px 8px', fontSize: 13, fontWeight: 600, border: '1px solid var(--primary)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text-primary)', outline: 'none' }} />
                      ) : (
                        <CCName style={{ textDecoration: disabled ? 'line-through' : 'none' }}>
                          {wf.name || `Workflow #${wf.id}`}
                        </CCName>
                      )}
                      <CCMeta>
                        {nodeCount(wf)} nœuds
                        {wf.workstation_name && <> • <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{wf.workstation_name}</span></>}
                        {wf.updated_at && ` • ${new Date(wf.updated_at).toLocaleDateString('fr-FR')}`}
                        {wf.execution_count > 0 && ` • ${wf.execution_count}×`}
                      </CCMeta>
                      {/* Schedule + Last execution info */}
                      {(scheduleInfo || lastExecEnd) && (
                        <div style={{ display: 'flex', gap: 12, marginTop: 3, flexWrap: 'wrap' }}>
                          {scheduleInfo && (
                            <span style={{ fontSize: 10, color: '#6366f1', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                              <Calendar size={10} /> {scheduleInfo}
                            </span>
                          )}
                          {lastExecEnd && (
                            <span style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                              <Clock size={10} /> Dernière fin: {new Date(lastExecEnd).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {exec?.status === 'running' && exec.steps && (
                      <CCSteps>
                        {exec.steps.map((s, j) => <CCStepDot key={j} $s={s.status} />)}
                      </CCSteps>
                    )}
                    <CCBadge $status={status}>
                      {status === 'running' ? '▶ En cours' : status === 'scheduled' ? '⏰ Planifié' : status === 'disabled' ? '⏹ Désactivé' : '⏸ Inactif'}
                    </CCBadge>
                    <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                      <button onClick={(e) => startRename(e, wf)} title="Renommer"
                        style={{ background: 'var(--surface)', border: 'none', borderRadius: 6, padding: 5, cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex' }}>
                        <Edit3 size={13} />
                      </button>
                      <button onClick={(e) => handleDelete(e, wf.id, wf.name)} title="Supprimer"
                        style={{ background: 'rgba(239,68,68,.08)', border: 'none', borderRadius: 6, padding: 5, cursor: 'pointer', color: '#ef4444', display: 'flex' }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </CCCard>
                );
              })}
            </CCGrid>
          )}
        </CCSection>

        {/* Execution History (merged with running executions) */}
        <CCSection>
          <CCSectionHeader>
            <CCSectionTitle><History size={15} /> Historique des exécutions</CCSectionTitle>
            <CCSectionCount>{runningNow.length > 0 && <span style={{ color: '#f59e0b', marginRight: 4 }}>{runningNow.length} en cours •</span>} {history.length} total</CCSectionCount>
          </CCSectionHeader>

          {/* ── Running/Paused executions at the top ── */}
          {runningNow.map((exec, i) => {
            const wf = workflows.find(w => String(w.id) === String(exec.workflowId));
            const elapsed = exec.startedAt ? Date.now() - new Date(exec.startedAt).getTime() : 0;
            const isPaused = exec.status === 'paused';
            const isRunning = exec.status === 'running';
            const liveSteps = exec.steps || [];
            const liveExpandKey = `live_${exec.workflowId}`;
            const isExpanded = expandedExecId === liveExpandKey;
            return (
              <div key={liveExpandKey}>
                <CCHistoryRow $running={isRunning} $paused={isPaused}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setExpandedExecId(isExpanded ? null : liveExpandKey)}>
                  <CCRunningPulse style={isPaused ? { background: '#6366f1' } : {}} />
                  <span style={{ fontSize: 10, color: 'var(--text-secondary)', whiteSpace: 'nowrap', minWidth: 80 }}>
                    {exec.startedAt ? new Date(exec.startedAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 700, color: isPaused ? '#6366f1' : '#f59e0b' }}>
                      {wf?.name || exec.workflowName || `Workflow #${exec.workflowId}`}
                    </span>
                    {exec.currentStep && (
                      <span style={{ fontSize: 10, color: 'var(--text-secondary)', marginLeft: 8 }}>
                        Étape: {exec.currentStep}
                      </span>
                    )}
                  </div>
                  {liveSteps.length > 0 && (
                    <CCSteps>
                      {liveSteps.map((s, j) => <CCStepDot key={j} $s={s.status} />)}
                      <span style={{ fontSize: 10, color: 'var(--text-secondary)', marginLeft: 4 }}>
                        {liveSteps.filter(s => s.status === 'completed').length}/{liveSteps.length}
                      </span>
                    </CCSteps>
                  )}
                  {elapsed > 0 && (
                    <span style={{ fontSize: 11, color: isRunning ? '#f59e0b' : '#6366f1', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {formatDuration(elapsed)}
                    </span>
                  )}
                  {/* Control buttons */}
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    {isRunning && (
                      <button onClick={(e) => handlePauseWorkflow(e, exec.workflowId)}
                        title="Mettre en pause"
                        style={{ background: 'rgba(99,102,241,.12)', border: '1px solid rgba(99,102,241,.25)', borderRadius: 6, padding: '3px 8px', color: '#6366f1', fontSize: 10, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Pause size={11} /> Pause
                      </button>
                    )}
                    {isPaused && (
                      <button onClick={(e) => handleResumeWorkflow(e, exec.workflowId)}
                        title="Reprendre"
                        style={{ background: 'rgba(34,197,94,.12)', border: '1px solid rgba(34,197,94,.25)', borderRadius: 6, padding: '3px 8px', color: '#22c55e', fontSize: 10, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Play size={11} /> Reprendre
                      </button>
                    )}
                    <button onClick={(e) => handleStopWorkflow(e, exec.workflowId)}
                      title="Arrêter"
                      style={{ background: 'rgba(239,68,68,.12)', border: '1px solid rgba(239,68,68,.25)', borderRadius: 6, padding: '3px 8px', color: '#ef4444', fontSize: 10, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Square size={11} /> Stop
                    </button>
                  </div>
                  <CCBadge $status={isPaused ? 'cancelled' : 'running'}>
                    {isPaused ? '⏸ Pause' : '▶ En cours'}
                  </CCBadge>
                  <ChevronRight size={14} color="var(--text-secondary)"
                    style={{ transition: 'transform .15s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', flexShrink: 0 }} />
                </CCHistoryRow>
                {isExpanded && (
                  <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--card-border)', padding: '14px 18px 14px 46px' }}>
                    <div style={{ display: 'flex', gap: 24, marginBottom: 12, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2 }}>Début</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                          {exec.startedAt ? new Date(exec.startedAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2 }}>Durée</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>{elapsed > 0 ? formatDuration(elapsed) : '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2 }}>Étapes</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                          {liveSteps.filter(s => s.status === 'completed').length}/{liveSteps.length} réussies
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Étapes en direct</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {liveSteps.map((step, si) => {
                        const stepDuration = step.startedAt && step.completedAt ? new Date(step.completedAt) - new Date(step.startedAt) : 0;
                        const scriptLabel = step.scriptType || step.type;
                        const scriptColor = scriptLabel === 'instagram-interact' || scriptLabel === 'interact' ? '#a855f7' :
                          scriptLabel === 'instagram-scrape' || scriptLabel === 'scrape' ? '#3b82f6' :
                          scriptLabel === 'instagram-unfollow' || scriptLabel === 'unfollow' ? '#f97316' :
                          scriptLabel === 'restarter' || scriptLabel === 'restart-connection' ? '#f59e0b' :
                          scriptLabel === 'scheduler' ? '#6366f1' : 'var(--text-secondary)';
                        return (
                          <div key={si} style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px',
                            background: step.status === 'running' ? 'rgba(245,158,11,.06)' : 'var(--card-bg)',
                            borderRadius: 6, border: `1px solid ${step.status === 'running' ? 'rgba(245,158,11,.2)' : 'var(--card-border)'}`,
                          }}>
                            <span style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 700, width: 20, textAlign: 'center' }}>{si + 1}</span>
                            {step.status === 'completed' ? <CheckCircle2 size={13} color="#22c55e" /> :
                             step.status === 'running' ? <Activity size={13} color="#f59e0b" style={{ animation: 'spin 2s linear infinite' }} /> :
                             step.status === 'failed' ? <XCircle size={13} color="#ef4444" /> :
                             step.status === 'cancelled' ? <Square size={13} color="#94a3b8" /> :
                             <Clock size={13} color="#94a3b8" />}
                            <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{step.label || step.type}</span>
                            {step.username && <span style={{ fontSize: 10, color: 'var(--primary)', fontWeight: 600 }}>@{step.username}</span>}
                            {stepDuration > 0 && <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--primary)' }}>{formatDuration(stepDuration)}</span>}
                            <span style={{ fontSize: 9, fontWeight: 700, color: scriptColor, background: `${scriptColor}18`, padding: '2px 6px', borderRadius: 4 }}>
                              {scriptLabel === 'instagram-interact' ? 'interact' : scriptLabel === 'instagram-scrape' ? 'scrape' : scriptLabel === 'instagram-unfollow' ? 'unfollow' : scriptLabel}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* ── Persisted history ── */}
          {history.length === 0 && runningNow.length === 0 ? (
            <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '30px 0', fontSize: 12 }}>
              Aucune exécution enregistrée
            </div>
          ) : (
            <div>
              {history.slice(0, 30).map((h, i) => {
                const duration = h.started_at && h.completed_at
                  ? new Date(h.completed_at) - new Date(h.started_at) : 0;
                const stepsArr = typeof h.steps === 'string' ? JSON.parse(h.steps || '[]') : (h.steps || []);
                const resultsArr = typeof h.results === 'string' ? JSON.parse(h.results || '[]') : (h.results || []);
                const isExpanded = expandedExecId === (h.id || i);
                const typeCounts = {};
                resultsArr.forEach(r => {
                  const t = r.type || 'unknown';
                  typeCounts[t] = (typeCounts[t] || 0) + 1;
                });
                return (
                  <div key={h.id || i}>
                    <CCHistoryRow style={{ cursor: 'pointer' }}
                      onClick={() => setExpandedExecId(isExpanded ? null : (h.id || i))}>
                      <span style={{ fontSize: 10, color: 'var(--text-secondary)', whiteSpace: 'nowrap', minWidth: 80 }}>
                        {h.started_at ? new Date(h.started_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </span>
                      {h.status === 'completed' || h.status === 'completed_with_errors' ? <CheckCircle2 size={14} color="#22c55e" /> :
                       h.status === 'failed' ? <XCircle size={14} color="#ef4444" /> :
                       h.status === 'cancelled' ? <Square size={14} color="#eab308" /> :
                       <Clock size={14} color="#94a3b8" />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                          {h.workflow_name || `Workflow #${h.workflow_id}`}
                        </span>
                      </div>
                      {stepsArr.length > 0 && (
                        <CCSteps>
                          {stepsArr.map((s, j) => <CCStepDot key={j} $s={s.status} />)}
                        </CCSteps>
                      )}
                      {duration > 0 && (
                        <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {formatDuration(duration)}
                        </span>
                      )}
                      <CCBadge $status={h.status === 'completed_with_errors' ? 'running' : h.status}>
                        {h.status === 'completed' ? 'OK' : h.status === 'completed_with_errors' ? 'Partiel' : h.status === 'failed' ? 'Erreur' : h.status === 'cancelled' ? 'Arrêté' : h.status}
                      </CCBadge>
                      <ChevronRight size={14} color="var(--text-secondary)"
                        style={{ transition: 'transform .15s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', flexShrink: 0 }} />
                    </CCHistoryRow>

                    {isExpanded && (
                      <div ref={el => { if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })); }}
                        style={{ background: 'var(--surface)', borderBottom: '1px solid var(--card-border)', padding: '14px 18px 14px 46px' }}>
                        {/* Timing info */}
                        <div style={{ display: 'flex', gap: 24, marginBottom: 12, flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2 }}>Début</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                              {h.started_at ? new Date(h.started_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2 }}>Fin</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                              {h.completed_at ? new Date(h.completed_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2 }}>Durée</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>
                              {duration > 0 ? formatDuration(duration) : '—'}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2 }}>Étapes</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                              {stepsArr.filter(s => s.status === 'completed').length}/{stepsArr.length} réussies
                            </div>
                          </div>
                        </div>

                        {/* Action type summary */}
                        {Object.keys(typeCounts).length > 0 && (
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Types d'actions</div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {Object.entries(typeCounts).map(([type, count]) => (
                                <span key={type} style={{
                                  padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                                  background: 'var(--card-bg)', border: '1px solid var(--card-border)', color: 'var(--text-primary)'
                                }}>
                                  {type} <span style={{ color: 'var(--primary)', fontWeight: 800 }}>×{count}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Step-by-step detail */}
                        <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Détail des étapes</div>
                        {/* Column header — reordered: #, status, Début, Fin, Compte, Durée, Étape, Type */}
                        <div style={{
                          display: 'grid', gridTemplateColumns: '24px 16px 50px 50px 120px 54px 1fr 80px',
                          gap: 8, padding: '4px 10px', fontSize: 9, fontWeight: 700, color: 'var(--text-secondary)',
                          textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: '1px solid var(--card-border)', marginBottom: 2
                        }}>
                          <span>#</span>
                          <span></span>
                          <span>Début</span>
                          <span>Fin</span>
                          <span>Compte</span>
                          <span>Durée</span>
                          <span>Étape</span>
                          <span>Type</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {stepsArr.map((step, si) => {
                            const stepDuration = step.startedAt && step.completedAt
                              ? new Date(step.completedAt) - new Date(step.startedAt) : 0;
                            const scriptLabel = step.scriptType || step.session?.sessionType || step.type;
                            const isInstagram = ['instagram-interact', 'interact', 'instagram-scrape', 'scrape', 'instagram-unfollow', 'unfollow'].includes(scriptLabel);
                            const isRestart = scriptLabel === 'restarter' || scriptLabel === 'restart-connection';
                            const scriptColor = scriptLabel === 'instagram-interact' || scriptLabel === 'interact' ? '#a855f7' :
                              scriptLabel === 'instagram-scrape' || scriptLabel === 'scrape' ? '#3b82f6' :
                              scriptLabel === 'instagram-unfollow' || scriptLabel === 'unfollow' ? '#f97316' :
                              isRestart ? '#f59e0b' :
                              scriptLabel === 'scheduler' ? '#6366f1' :
                              scriptLabel === 'delay' ? '#94a3b8' : 'var(--text-secondary)';
                            const sess = step.session;
                            const displayUsername = step.username || null;
                            const displayDevice = step.deviceName || sess?.deviceName || step.deviceId || null;
                            // IP data from restart signal (stored in step.output.restart.signal)
                            const restartSignal = step.output?.restart?.signal || null;
                            const ipBefore = restartSignal?.ip_before || null;
                            const ipAfter = restartSignal?.ip_after || null;
                            // Profile pic: from step data, from username path, or from session
                            const profilePicSrc = step.profilePicUrl || (displayUsername ? getLocalProfilePicUrl(displayUsername) : null);
                            const hasSubRow = sess || (isRestart && (ipBefore || ipAfter));
                            return (
                              <div key={si}>
                                <div style={{
                                  display: 'grid', gridTemplateColumns: '24px 16px 50px 50px 120px 54px 1fr 80px',
                                  gap: 8, alignItems: 'center', padding: '5px 10px',
                                  background: 'var(--card-bg)', borderRadius: hasSubRow ? '6px 6px 0 0' : 6, border: '1px solid var(--card-border)',
                                  borderBottom: hasSubRow ? 'none' : undefined
                                }}>
                                  {/* # */}
                                  <span style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 700, textAlign: 'center' }}>{si + 1}</span>
                                  {/* Status icon */}
                                  <span style={{ display: 'flex', alignItems: 'center' }}>
                                    {step.status === 'completed' ? <CheckCircle2 size={13} color="#22c55e" /> :
                                     step.status === 'failed' ? <XCircle size={13} color="#ef4444" /> :
                                     step.status === 'cancelled' ? <Square size={13} color="#94a3b8" /> :
                                     <Clock size={13} color="#94a3b8" />}
                                  </span>
                                  {/* Start time */}
                                  <span style={{ fontSize: 10, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                                    {step.startedAt ? new Date(step.startedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'}
                                  </span>
                                  {/* End time */}
                                  <span style={{ fontSize: 10, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                                    {step.completedAt ? new Date(step.completedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'}
                                  </span>
                                  {/* Account (with profile pic + IG icon) — right after Fin */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                                    {isInstagram && (
                                      <Instagram size={12} color="#E1306C" style={{ flexShrink: 0 }} />
                                    )}
                                    {profilePicSrc && displayUsername && (
                                      <img src={profilePicSrc} alt=""
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                        style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1.5px solid var(--card-border)' }} />
                                    )}
                                    <span style={{ fontSize: 10, color: displayUsername ? 'var(--primary)' : 'var(--text-secondary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                      title={displayUsername || '—'}>
                                      {displayUsername ? `@${displayUsername}` : '—'}
                                    </span>
                                  </div>
                                  {/* Duration */}
                                  <span style={{ fontSize: 10, fontWeight: 600, color: stepDuration > 0 ? 'var(--primary)' : 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                                    {stepDuration > 0 ? formatDuration(stepDuration) : '—'}
                                  </span>
                                  {/* Label + device + error */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {step.label || step.type}
                                    </span>
                                    {displayDevice && (
                                      <span style={{ fontSize: 9, color: 'var(--text-secondary)', background: 'var(--surface)', padding: '1px 5px', borderRadius: 3 }}
                                        title={step.deviceId || displayDevice}>
                                        {displayDevice.length > 16 ? displayDevice.substring(0, 16) : displayDevice}
                                      </span>
                                    )}
                                    {step.error && (
                                      <span style={{ fontSize: 9, color: '#ef4444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}
                                        title={step.error}>
                                        {step.error}
                                      </span>
                                    )}
                                  </div>
                                  {/* Script type badge */}
                                  <span style={{
                                    fontSize: 9, fontWeight: 700, color: scriptColor, background: `${scriptColor}18`,
                                    padding: '2px 6px', borderRadius: 4, textAlign: 'center', overflow: 'hidden',
                                    textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                  }}>
                                    {scriptLabel === 'instagram-interact' || scriptLabel === 'interact' ? 'interact' :
                                     scriptLabel === 'instagram-scrape' || scriptLabel === 'scrape' ? 'scrape' :
                                     scriptLabel === 'instagram-unfollow' || scriptLabel === 'unfollow' ? 'unfollow' :
                                     scriptLabel === 'restart-connection' || scriptLabel === 'restarter' ? 'restart' :
                                     scriptLabel}
                                  </span>
                                </div>
                                {/* Restart IP sub-row */}
                                {isRestart && (ipBefore || ipAfter) && (
                                  <div style={{
                                    display: 'flex', gap: 14, padding: '4px 10px 5px 50px', flexWrap: 'wrap',
                                    background: 'var(--card-bg)', borderRadius: sess ? '0' : '0 0 6px 6px', border: '1px solid var(--card-border)',
                                    borderTop: '1px dashed var(--card-border)', fontSize: 10, color: 'var(--text-secondary)'
                                  }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <Monitor size={10} color="#f59e0b" />
                                      <b style={{ color: 'var(--text-primary)' }}>IP avant:</b>
                                      <span style={{ color: ipBefore ? '#f97316' : 'var(--text-secondary)', fontFamily: 'monospace', fontWeight: 700 }}>{ipBefore || '—'}</span>
                                    </span>
                                    <span style={{ color: 'var(--text-secondary)' }}>→</span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <b style={{ color: 'var(--text-primary)' }}>IP après:</b>
                                      <span style={{ color: ipAfter ? '#22c55e' : 'var(--text-secondary)', fontFamily: 'monospace', fontWeight: 700 }}>{ipAfter || '—'}</span>
                                    </span>
                                    {ipBefore && ipAfter && ipBefore !== ipAfter && (
                                      <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 9, background: 'rgba(34,197,94,.1)', padding: '1px 6px', borderRadius: 3 }}>IP changée</span>
                                    )}
                                    {ipBefore && ipAfter && ipBefore === ipAfter && (
                                      <span style={{ color: '#ef4444', fontWeight: 700, fontSize: 9, background: 'rgba(239,68,68,.1)', padding: '1px 6px', borderRadius: 3 }}>IP inchangée</span>
                                    )}
                                  </div>
                                )}
                                {/* Session stats sub-row */}
                                {sess && (
                                  <div style={{
                                    display: 'flex', gap: 10, padding: '4px 10px 5px 50px', flexWrap: 'wrap',
                                    background: 'var(--card-bg)', borderRadius: '0 0 6px 6px', border: '1px solid var(--card-border)',
                                    borderTop: '1px dashed var(--card-border)', fontSize: 10, color: 'var(--text-secondary)'
                                  }}>
                                    {sess.totalLikes > 0 && <span>❤️ <b style={{ color: '#ef4444' }}>{sess.totalLikes}</b> likes</span>}
                                    {sess.totalFollows > 0 && <span>➕ <b style={{ color: '#22c55e' }}>{sess.totalFollows}</b> follows</span>}
                                    {sess.totalUnfollows > 0 && <span>➖ <b style={{ color: '#f97316' }}>{sess.totalUnfollows}</b> unfollows</span>}
                                    {sess.totalGetProfile > 0 && <span>👤 <b style={{ color: '#3b82f6' }}>{sess.totalGetProfile}</b> profils</span>}
                                    {sess.totalStoryViews > 0 && <span>👁 <b style={{ color: '#8b5cf6' }}>{sess.totalStoryViews}</b> stories</span>}
                                    {sess.totalStoryLikes > 0 && <span>⭐ <b>{sess.totalStoryLikes}</b> story likes</span>}
                                    {sess.totalComments > 0 && <span>💬 <b>{sess.totalComments}</b> comments</span>}
                                    {sess.totalDms > 0 && <span>✉️ <b>{sess.totalDms}</b> DMs</span>}
                                    {sess.totalInteractions > 0 && <span style={{ marginLeft: 'auto', fontWeight: 700, color: 'var(--primary)' }}>Total: {sess.totalInteractions}</span>}
                                    {sess.totalInteractions === 0 && sess.totalLikes === 0 && sess.totalFollows === 0 && sess.totalGetProfile === 0 && (
                                      <span style={{ fontStyle: 'italic' }}>Aucune action enregistrée</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CCSection>

        {/* Chart: Executions over last 7 days — at bottom */}
        <CCSection>
          <CCSectionHeader>
            <CCSectionTitle><BarChart3 size={15} /> Exécutions (7 derniers jours)</CCSectionTitle>
            <CCSectionCount>{history.length} total</CCSectionCount>
          </CCSectionHeader>
          <CCChartWrap>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barGap={2}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,.03)' }} />
                <Bar dataKey="ok" stackId="a" fill="#22c55e" radius={[0,0,0,0]} />
                <Bar dataKey="fail" stackId="a" fill="#ef4444" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CCChartWrap>
        </CCSection>
      </CCMain>
    </CCDashboard>
  );
}

/* ═══════════════════════════════════════════════
   TAB WRAPPER — Builder | Workflows | Sessions
   ═══════════════════════════════════════════════ */

const WBTabBar = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 8px 16px;
  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);
`;

const WBTab = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  background: ${p => p.$active ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$active ? '#fff' : 'var(--text-secondary)'};
  &:hover { background: ${p => p.$active ? 'var(--primary)' : 'var(--surface-hover)'}; color: ${p => p.$active ? '#fff' : 'var(--text-primary)'}; }
`;

const WBTabContent = styled.div`
  flex: 1;
  overflow: auto;
`;

// Wrapper avec ReactFlowProvider + tabs
export default function WorkflowBuilder({ selectedDevice, onSelectDevice, setMirrorOpen }) {
  const [activeWBTab, setActiveWBTab] = useState('control');
  const [pendingLoadId, setPendingLoadId] = useState(null);

  const handleOpenWorkflow = useCallback((wfId) => {
    setPendingLoadId(wfId);
    setActiveWBTab('builder');
  }, []);

  const handleCreateNew = useCallback(() => {
    setPendingLoadId('__new__');
    setActiveWBTab('builder');
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 40px)' }}>
      <WBTabBar>
        <WBTab $active={activeWBTab === 'control'} onClick={() => setActiveWBTab('control')}>
          <Zap size={14} /> Dashboard
        </WBTab>
        <WBTab $active={activeWBTab === 'builder'} onClick={() => setActiveWBTab('builder')}>
          <GitBranch size={14} /> Builder
        </WBTab>
        <WBTab $active={activeWBTab === 'sessions'} onClick={() => setActiveWBTab('sessions')}>
          <CalendarDays size={14} /> Sessions
        </WBTab>
        <WBTab $active={activeWBTab === 'timeline'} onClick={() => setActiveWBTab('timeline')}>
          <GanttChart size={14} /> Timeline
        </WBTab>
        <WBTab $active={activeWBTab === 'flows'} onClick={() => setActiveWBTab('flows')}>
          <List size={14} /> Flows
        </WBTab>
      </WBTabBar>

      {activeWBTab === 'builder' && (
        <ReactFlowProvider>
          <WorkflowBuilderContent initialLoadId={pendingLoadId} onConsumeLoad={() => setPendingLoadId(null)} />
        </ReactFlowProvider>
      )}
      {activeWBTab === 'control' && (
        <WorkflowControlCenter onOpenWorkflow={handleOpenWorkflow} onCreateNew={handleCreateNew} onSelectDevice={onSelectDevice} setMirrorOpen={setMirrorOpen} />
      )}
      {activeWBTab === 'sessions' && (
        <WBTabContent>
          <Sessions selectedDevice={selectedDevice} />
        </WBTabContent>
      )}
      {activeWBTab === 'timeline' && (
        <WBTabContent style={{ display: 'flex', flexDirection: 'column' }}>
          <WorkflowSessionTimeline />
        </WBTabContent>
      )}
      {activeWBTab === 'flows' && (
        <WBTabContent style={{ display: 'flex', flexDirection: 'column' }}>
          <ScriptFlows selectedDevice={selectedDevice} />
        </WBTabContent>
      )}
    </div>
  );
}
